import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { WorkflowBuilderStoreService } from '../../services/workflow-builder-store.service';
import { buildLayout, LayoutNode } from '../../core/workflow-layout';
import { scaledCanvasSize } from '../../core/canvas-sizing';
import {
  isAuthorised,
  validate,
  Warning,
} from '../../core/workflow-validation';
import { WORKFLOW_VALIDATION_CONFIG } from '../../core/workflow-validation.config';
import { fromJson, toJson } from '../../core/workflow-mapper';
import {
  DEFAULT_STATE_COLOUR,
  fallbackStateColour,
  resolveStateColour,
} from '../../core/catalogs';
import {
  IAction,
  IBuilderModel,
  IEndState,
  IStateMeta,
  ITransition,
} from '../../models/workflow-builder.model';
import {
  InspectorActionDeleteRequest,
  InspectorTarget,
} from '../inspector/inspector.component';
import { ServiceChangeRequestService } from '../../../../../core/services/service-change-request.service';
import {
  classifyState,
  WorkflowStateKind,
} from '../../core/workflow-state-classification';
import { autoLayoutWorkflow } from '../../core/workflow-auto-layout';
import {
  buildWorkflowVisualGraph,
  outcomeNodeKey,
  WorkflowOutcomeRef,
  WorkflowVisualGraph,
  WorkflowVisualNode,
} from '../../core/workflow-visual-graph';

export type AnchorName =
  | 't1'
  | 't2'
  | 't3'
  | 'r1'
  | 'r2'
  | 'r3'
  | 'b1'
  | 'b2'
  | 'b3'
  | 'l1'
  | 'l2'
  | 'l3';
export interface FreeEdge {
  id: string;
  tid: string;
  srcCode: string;
  dstCode: string;
  srcKey: string;
  dstKey: string;
  srcName: AnchorName;
  dstName: AnchorName;
  sx: number;
  sy: number;
  ex: number;
  ey: number;
  d: string;
  mx: number;
  my: number;
  label: string;
  actor: string;
  colour: string;
  isSystem: boolean;
  unauthorized: boolean;
  /** For a two-outcome (conditional) transition: '①' = endStateOne, '②' = endStateTwo, '' = single outcome. */
  outcome: '' | '①' | '②';
  /** For guarded transitions routed through a ◆ decision: 'false' = ◆→①, 'true' = ◆→②, '' = normal/event edge. */
  branch: '' | 'true' | 'false';
}

/**
 * Container for the v2 workflow builder. The ONLY component that pulls in the
 * shared stylesheet (ViewEncapsulation.None) and wraps its template in `.iwb-v2`.
 * Presentational children render the model; the store owns state.
 */
@Component({
  selector: 'irembogov-workflow-builder-v2',
  templateUrl: './workflow-builder.component.html',
  styleUrls: ['../../styles/workflow-builder-v2.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [WorkflowBuilderStoreService],
})
export class WorkflowBuilderComponent
  implements OnChanges, OnDestroy, AfterViewChecked
{
  @Input() workflowJson: unknown;
  /** Opens the fullscreen Monaco editor when the host selects its Code Editor tab. */
  @Input() jsonEditorInitiallyOpen = false;
  /** Service form fields — passed straight through to the reused notification / receipt managers. */
  @Input() formFields: unknown;
  /** Organisation id — passed straight through to the reused office-assignment manager. */
  @Input() organizationId?: string;
  @Output() workflowJsonChange = new EventEmitter<unknown>();

  model: IBuilderModel = { transitions: [], states: [], topRaw: {} };
  visualGraph: WorkflowVisualGraph = { nodes: [], edges: [] };
  layout: LayoutNode[] = [];
  json: unknown = { workflow: [] };
  inspector: InspectorTarget | null = null;
  composerFrom: string | null = null;
  /** Whether the add-event composer is open. */
  composerOpen = false;
  /** True when composing the mandatory first event (NEW → APPLY) → lock the event to APPLY. */
  composerFirst = false;
  /** True when the composer opens in edit-before-create ("duplicate a copied event") mode. */
  composerPaste = false;
  /** Where to render the add-event composer (near the click; clamped to viewport). */
  composerPos = { x: 0, y: 90 };
  /**
   * Max height for the floating composer so its bottom (the Add event / Cancel buttons) can
   * never sit below the viewport fold — the panel scrolls internally instead of getting stuck.
   */
  get composerMaxH(): number {
    return Math.max(220, window.innerHeight - this.composerPos.y - 16);
  }
  /** Which reused wizard/v1 manager (if any) is open as a drawer. */
  manager: 'notification' | 'office' | 'receipt' | null = null;
  warnings: Warning[] = [];
  /** State briefly flashed after clicking its warning (so the user sees the source). */
  focusedStateCode: string | null = null;
  private focusClearTimer: ReturnType<typeof setTimeout> | null = null;
  showTech = false;

  // ── view controls (M7B: fullscreen + zoom/pan over the auto-layout) ──
  @ViewChild('canvasEl') canvasRef?: ElementRef<HTMLElement>;
  @ViewChild('flowEl') flowRef?: ElementRef<HTMLElement>;
  @ViewChild('flowWrap') flowWrapRef?: ElementRef<HTMLElement>;
  scale = 1;
  /** Zoom bounds. The floor is low enough that even a large workflow can be
      framed to fit — a big diagram would otherwise bottom out at the old 25%
      and stay bigger than the viewport. */
  private readonly MIN_SCALE = 0.1;
  private readonly MAX_SCALE = 3;
  isFullscreen = false;
  showJsonEditor = false;
  // Monaco loads lazily (~seconds) the first time an editor mounts, which left the
  // state JSON tab / fullscreen editor blank on first open. Warm it up in the
  // background with a hidden editor so those open instantly; drop it once loaded.
  monacoWarm = false;
  monacoWarmupOptions = {
    theme: 'vs',
    language: 'json',
    automaticLayout: false,
    minimap: { enabled: false },
    readOnly: true,
    lineNumbers: 'off' as const,
  };
  onMonacoWarm(): void {
    this.monacoWarm = true;
    this.cdr.markForCheck();
  }

  // ── free mode (7B.3): absolutely-positioned, draggable state boxes (DEFAULT) ──
  freeMode = true;
  /** Tidy-up flow direction: top-down (vertical) or left-right (horizontal, fits wide graphs). */
  tidyDir: 'vertical' | 'horizontal' = 'vertical';
  freePos: Record<string, { x: number; y: number }> = {};
  freeSize = { w: 900, h: 600 };
  /** Render offset so the left/top-most box sits at MARGIN — lets boxes move left without a "wall". */
  private off = { x: 0, y: 0 };
  private readonly MARGIN = 48;
  private seeded = false;
  private pendingTidy = false;
  private initialViewportFramePending = false;
  private initialViewportFramed = false;
  layoutBusy = false;
  private autoLayoutRequest = 0;
  /** Code of the most recently added state — scrolled into view once it renders. */
  private justAddedCode: string | null = null;
  /** Guards a single extra measure pass so a just-painted node's edges snap to its real size. */
  private remeasureScheduled = false;
  /** Remember where/which-direction the last add-event was triggered, to place the new state. */
  private lastComposerFrom: string | null = null;
  private lastComposerDir = { dx: 0, dy: 1 };
  freeEdges: FreeEdge[] = [];
  /**
   * Render-space box (centre + size) of each guarded transition's ◆ decision diamond, keyed
   * by `cond:<tid>`. Lets the diamond participate in the generic anchor/re-route geometry as a
   * virtual node (it is NOT draggable / not persisted — position is derived from its states).
   */
  private condBox: Record<
    string,
    { cx: number; cy: number; w: number; h: number }
  > = {};
  /** UI-only anchor overrides per edge (re-routing); NOT written to workflow.json. */
  freeEdgeAnchors: Record<string, { src?: AnchorName; dst?: AnchorName }> = {};
  /**
   * Node key currently hovered as a MOVE drop target while dragging an edge endpoint over a
   * DIFFERENT node (re-anchoring the arrow onto another state, vs. the same-box re-route).
   * UI-only highlight — the actual move is confirmed on release.
   */
  moveDropKey: string | null = null;
  /** Named anchor positions (fraction of the box) — connection points around a state. */
  readonly anchorNames: AnchorName[] = [
    't1',
    't2',
    't3',
    'r1',
    'r2',
    'r3',
    'b3',
    'b2',
    'b1',
    'l3',
    'l2',
    'l1',
  ];
  private readonly anchorFr: Record<AnchorName, { fx: number; fy: number }> = {
    t1: { fx: 0.25, fy: 0 },
    t2: { fx: 0.5, fy: 0 },
    t3: { fx: 0.75, fy: 0 },
    b1: { fx: 0.25, fy: 1 },
    b2: { fx: 0.5, fy: 1 },
    b3: { fx: 0.75, fy: 1 },
    l1: { fx: 0, fy: 0.25 },
    l2: { fx: 0, fy: 0.5 },
    l3: { fx: 0, fy: 0.75 },
    r1: { fx: 1, fy: 0.25 },
    r2: { fx: 1, fy: 0.5 },
    r3: { fx: 1, fy: 0.75 },
  };
  private nodeSizes: Record<string, { w: number; h: number }> = {};
  private _edgesKey = '';
  private panning = false;
  private panStart = { x: 0, y: 0, sl: 0, st: 0 };
  private sizingDirty = true;

  private sub = new Subscription();
  private loaded = false;
  /**
   * The builder owns the workflow state after the first load. The host re-binds our emitted
   * value back into [workflowJson] on every edit (echo), autosave round-trip and json-editor
   * sync; reloading those would regenerate transition ids (closing the inspector) and re-seed
   * positions (re-running the auto-layout). So we load exactly once and ignore later inputs.
   */
  private inputLoaded = false;

  /** Full application-state catalog (code + label + backend colour) for the destination picker. */
  apiStates: IStateMeta[] = [];

  constructor(
    private store: WorkflowBuilderStoreService,
    private cdr: ChangeDetectorRef,
    private crs: ServiceChangeRequestService
  ) {
    this.sub.add(
      this.crs.getApplicationStates().subscribe({
        next: (res: any) => {
          const list: any[] = res?.data?.content ?? res?.data ?? [];
          this.apiStates = list
            // Backend contract: `stateName` is the machine CODE, `stateLabel` the human
            // name, `stateColor` a Bootstrap class. (Older field names kept as fallbacks.)
            .filter(s => s && (s.stateName ?? s.stateCode))
            .map(s => ({
              code: s.stateName ?? s.stateCode,
              name: s.stateLabel ?? s.stateName ?? s.stateCode,
              colour: resolveStateColour(
                s.stateColor ?? s.stateColour ?? s.colour ?? s.color
              ),
            }));
          this.cdr.markForCheck();
        },
        error: () => {
          /* non-fatal: the "New state" picker stays empty and shows its
             "No system states available" message until the catalog loads. */
        },
      })
    );
    this.sub.add(
      this.store.model$.subscribe(m => {
        const previousVisualGraph = this.visualGraph;
        this.model = m;
        this.visualGraph = buildWorkflowVisualGraph(m);
        this.layout = buildLayout(m);
        this.json = toJson(m);
        this.warnings = validate(m);
        this.sizingDirty = true; // layout changed → re-measure the scaled scroll area
        // Seed only once the model actually has states. The store is a BehaviorSubject, so the
        // constructor subscription fires first with the EMPTY initial model — seeding then would
        // mark `seeded` (and pendingTidy) before the real workflow loads, so its saved positions
        // would never be honoured.
        if (this.freeMode && !this.seeded && m.states.length > 0) {
          this.seeded = true;
          this.seedFreePositions();
        } else if (this.freeMode && this.seeded) {
          this.reconcileVisualPositions(previousVisualGraph);
          this.placeMissingPositions();
          this.scheduleFreeRecompute();
        }
        // Emit user edits to the host save path; skip the initial load emit.
        if (this.loaded) {
          this.workflowJsonChange.emit(this.json);
        }
        this.loaded = true;
        this.cdr.markForCheck();
      })
    );
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['jsonEditorInitiallyOpen']?.currentValue) {
      this.showJsonEditor = true;
    }
    if (!ch['workflowJson']) return;
    // Load the workflow ONCE. The host continuously re-binds our own emitted value back into
    // [workflowJson] (edit echoes, autosave round-trips, json-editor sync). Reloading those would
    // regenerate transition ids — closing the open inspector — and re-seed free positions,
    // making the states visibly rearrange on their own. After the first real load the builder
    // is the source of truth; later parent updates are ignored. (A fresh workflow remounts this
    // component, so it re-loads correctly then.)
    if (this.inputLoaded) return;
    const workflowJson = this.workflowJsonForLoad(this.workflowJson);
    if (!workflowJson) return; // wait for the real workflow document
    this.inputLoaded = true;
    this.store.load(workflowJson);
  }

  private workflowJsonForLoad(raw: unknown): unknown | null {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
      return null;
    }
    const doc = raw as Record<string, unknown>;
    if (!Object.prototype.hasOwnProperty.call(doc, 'workflow')) {
      return null;
    }
    if (Array.isArray(doc['workflow'])) {
      return raw;
    }
    if (doc['workflow'] == null) {
      return { ...doc, workflow: [] };
    }
    return null;
  }

  ngAfterViewChecked(): void {
    // Keep the scrollable area sized to the SCALED content so zoom-in can pan.
    if (this.sizingDirty) {
      const flow = this.flowRef?.nativeElement;
      const wrap = this.flowWrapRef?.nativeElement;
      if (flow && wrap) {
        // In free mode the nodes are absolutely positioned, so `.flow` can
        // measure as 0px wide. Reserve the free canvas size instead so the
        // graph starts in view and zoom still has a real scroll area.
        const size = scaledCanvasSize(
          this.freeMode,
          this.freeSize,
          this.scale,
          { w: flow.offsetWidth, h: flow.offsetHeight }
        );
        wrap.style.width = size.w + 'px';
        wrap.style.height = size.h + 'px';
        this.sizingDirty = false;
      }
    }
    if (this.initialViewportFramePending && this.canvasRef) {
      const code = this.initialStateCode();
      if (code && this.freePos[code]) {
        this.initialViewportFramePending = false;
        this.initialViewportFramed = true;
        this.frameInitialView();
      }
    }
  }
  ngOnDestroy(): void {
    this.autoLayoutRequest++;
    this.sub.unsubscribe();
  }

  // ── zoom / pan / fullscreen ──
  zoom(dir: 'in' | 'out' | 'reset'): void {
    if (dir === 'reset') {
      this.scale = 1;
    } else {
      const next = dir === 'in' ? this.scale * 1.15 : this.scale / 1.15;
      this.scale = Math.min(
        this.MAX_SCALE,
        Math.max(this.MIN_SCALE, +next.toFixed(3))
      );
    }
    this.sizingDirty = true;
  }
  /** Ctrl/Cmd + wheel = zoom-to-cursor (plain wheel keeps native scroll). */
  onWheel(e: WheelEvent): void {
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    const el = this.canvasRef?.nativeElement;
    if (!el) return;
    const prev = this.scale;
    const next = Math.min(
      this.MAX_SCALE,
      Math.max(
        this.MIN_SCALE,
        +(prev * (e.deltaY < 0 ? 1.1 : 1 / 1.1)).toFixed(3)
      )
    );
    if (next === prev) return;
    const rect = el.getBoundingClientRect();
    const px = e.clientX - rect.left + el.scrollLeft;
    const py = e.clientY - rect.top + el.scrollTop;
    const k = next / prev;
    this.scale = next;
    this.sizingDirty = true;
    // re-measure synchronously, then keep the cursor point stationary
    this.ngAfterViewChecked();
    el.scrollLeft = px * k - (e.clientX - rect.left);
    el.scrollTop = py * k - (e.clientY - rect.top);
  }
  /** Grab-to-pan on empty canvas (adjusts native scroll; ignores clicks on nodes/controls). */
  startPan(e: PointerEvent): void {
    if (e.button !== 0) return; // only left-button pans (right-click → context menu)
    const t = e.target as HTMLElement;
    // NOTE: include `.evlabel`/`.edge-labels`/`.endpoint` — without them a pointerdown on an event
    // label starts a pan and captures the pointer, so the label's (click) never fires (the click is
    // redirected to the canvas) and you can't open the event config. (Bug: event not clickable.)
    if (
      t.closest(
        '.box, .ev, .edge, .evlabel, .edge-labels, .endpoint, .cond, irembogov-iwb-event-label, irembogov-iwb-condition-node, .iwb-toolbar, .iwb-drawer-host, irembogov-iwb-add-event, button, input, textarea, select'
      )
    )
      return;
    const el = this.canvasRef?.nativeElement;
    if (!el) return;
    this.panning = true;
    this.panStart = {
      x: e.clientX,
      y: e.clientY,
      sl: el.scrollLeft,
      st: el.scrollTop,
    };
    el.classList.add('grabbing');
    el.setPointerCapture?.(e.pointerId);
  }
  onPanMove(e: PointerEvent): void {
    if (!this.panning) return;
    const el = this.canvasRef?.nativeElement;
    if (!el) return;
    el.scrollLeft = this.panStart.sl - (e.clientX - this.panStart.x);
    el.scrollTop = this.panStart.st - (e.clientY - this.panStart.y);
  }
  endPan(e: PointerEvent): void {
    if (!this.panning) return;
    this.panning = false;
    const el = this.canvasRef?.nativeElement;
    el?.classList.remove('grabbing');
    el?.releasePointerCapture?.(e.pointerId);
  }
  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    this.sizingDirty = true;
  }

  /** Open the fullscreen workflow.json editor seeded with the current workflow. */
  openJsonEditor(): void {
    this.showJsonEditor = true;
  }

  /** Apply JSON edited in the fullscreen editor back into the builder. */
  onJsonEditorSave(parsed: unknown): void {
    this.showJsonEditor = false;
    // JSON save replaces the document, so its canvas must not inherit positions or
    // viewport state from the workflow that was open before the editor.
    this.autoLayoutRequest++;
    this.seeded = false;
    this.freePos = {};
    this.nodeSizes = {};
    this.freeEdgeAnchors = {};
    this.initialViewportFramePending = false;
    this.initialViewportFramed = false;
    // Reload the model from the edited JSON; the model$ subscription recomputes
    // `json` and echoes it out via (workflowJsonChange), same as any other edit.
    this.store.load(parsed);
  }

  /**
   * Semantic guard for the fullscreen JSON editor. Returns an error message (which
   * keeps the editor open) or null when `parsed` is a structurally valid workflow.
   *
   * Without this, `JSON.parse` alone accepts `{}` — which the mapper reads as an
   * empty workflow — so a stray edit would silently wipe the visual builder;
   * malformed transitions would likewise reset the store to an empty model.
   * Bound as a field so it can be passed to the child editor via [validate].
   */
  validateWorkflowJson = (parsed: unknown): string | null => {
    if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return 'the root must be a JSON object with a "workflow" array.';
    }
    const hasWorkflow = Object.prototype.hasOwnProperty.call(
      parsed,
      'workflow'
    );
    if (!hasWorkflow) {
      return 'missing a "workflow" array — refusing to save so the workflow is not cleared.';
    }
    const wf = (parsed as { workflow?: unknown }).workflow;
    if (wf === null) {
      return null;
    }
    if (!Array.isArray(wf)) {
      return 'missing a "workflow" array — refusing to save so the workflow is not cleared.';
    }
    for (let i = 0; i < wf.length; i++) {
      const t = wf[i] as Record<string, unknown> | null;
      const where = `transition #${i + 1}`;
      if (t == null || typeof t !== 'object')
        return `${where} is not an object.`;
      if (typeof t['startState'] !== 'string' || !t['startState']) {
        return `${where} is missing a "startState".`;
      }
      if (typeof t['event'] !== 'string' || !t['event']) {
        return `${where} is missing an "event".`;
      }
      const one = t['endStateOne'] as Record<string, unknown> | null;
      if (one == null || typeof one !== 'object' || !one['stateCode']) {
        return `${where} is missing a valid "endStateOne.stateCode".`;
      }
    }
    // Final smoke test: the mapper must be able to parse it without throwing.
    try {
      fromJson(parsed);
    } catch (e) {
      return `the workflow could not be parsed (${(e as Error).message}).`;
    }
    return null;
  };

  // ── template helpers (container resolves model slices for the dumb children) ──
  endStateFor(
    ref: { tid: string; which: 'one' | 'two' } | null
  ): IEndState | null {
    if (!ref) return null;
    const t = this.transition(ref.tid);
    return t ? (ref.which === 'one' ? t.one : t.two) : null;
  }
  endStateForNode(node: WorkflowVisualNode): IEndState | null {
    return this.endStateFor(node.ref);
  }
  refForNode(node: WorkflowVisualNode): WorkflowOutcomeRef | null {
    return node.ref;
  }
  incomingPathFor(node: WorkflowVisualNode): string {
    if (!node.ref) return node.stateCode;
    const transition = this.transition(node.ref.tid);
    return transition
      ? `${transition.startState} → ${transition.event} → ${node.stateCode}`
      : node.stateCode;
  }
  transition(tid: string): ITransition {
    return this.model.transitions.find(t => t.id === tid) as ITransition;
  }
  stateName(code: string): string {
    const s = this.model.states.find(x => x.code === code);
    return s ? s.name : code;
  }
  /**
   * Resolve a state's display colour. The model only carries the default grey (the
   * workflow JSON has no colour), so fall back to the backend application-states
   * catalogue (`apiStates`) so a state shows its real colour once loaded from the BE.
   */
  colour(code: string): string {
    const s = this.model.states.find(x => x.code === code);
    if (s && s.colour && s.colour !== DEFAULT_STATE_COLOUR) return s.colour;
    const api = this.apiStates.find(a => a.code === code);
    const resolved = api?.colour ?? s?.colour;
    return resolved && resolved !== DEFAULT_STATE_COLOUR
      ? resolved
      : fallbackStateColour(code);
  }

  /** Colour for the NEW node on the empty-state preview — the conventional primary blue,
   *  falling back from the catalog colour so it never renders as the muted default. */
  get emptyStartColour(): string {
    const c = this.colour('NEW');
    return c === DEFAULT_STATE_COLOUR ? '#0063cf' : c;
  }
  stateKind(code: string): WorkflowStateKind {
    return classifyState(code, this.model);
  }
  isTerminal(code: string): boolean {
    return this.stateKind(code) === 'final';
  }
  isDeadEnd(code: string): boolean {
    return this.stateKind(code) === 'dead-end';
  }
  trackByIndex(i: number): number {
    return i;
  }
  trackByDecision(_: number, d: { tid: string }): string {
    return d.tid;
  }

  /** Measured size of a node, or a sensible estimate. Decision diamonds (cond:<tid>) are smaller. */
  private sizeOf(code: string): { w: number; h: number } {
    const m = this.nodeSizes[code];
    if (m) return m;
    if (code.startsWith('cond:')) return { w: 240, h: 74 };
    return { w: 258, h: this.estH(code) };
  }
  /** Centre point of a node in render coordinates (null if not yet positioned). */
  private nodeCenter(code: string): { x: number; y: number } | null {
    const p = this.freePos[code];
    if (!p) return null;
    const sz = this.sizeOf(code);
    return { x: p.x + this.off.x + sz.w / 2, y: p.y + this.off.y + sz.h / 2 };
  }

  /** Distance from a box centre to its edge along a unit direction (ux,uy). */
  private edgeDist(hw: number, hh: number, ux: number, uy: number): number {
    const tx = ux !== 0 ? hw / Math.abs(ux) : Infinity;
    const ty = uy !== 0 ? hh / Math.abs(uy) : Infinity;
    return Math.min(tx, ty);
  }

  /**
   * Render-space centre for a guarded transition's ◆ diamond: placed in the MIDDLE OF THE GAP
   * between the source box edge and the outcomes' box edge (clamped to keep clear of both), so
   * it never stacks on the source — works the same in vertical and horizontal layouts.
   */
  private condCenter(t: ITransition): { x: number; y: number } | null {
    if (!t.two) return null;
    const srcC = this.nodeCenter(t.startState);
    const oneKey = this.visualKeyForOutcome(t, 'one');
    const twoKey = this.visualKeyForOutcome(t, 'two');
    const oneC = this.nodeCenter(oneKey);
    const twoC = this.nodeCenter(twoKey);
    if (!srcC || !oneC || !twoC) return null;
    const vx = (oneC.x + twoC.x) / 2 - srcC.x;
    const vy = (oneC.y + twoC.y) / 2 - srcC.y;
    const len = Math.hypot(vx, vy) || 1;
    const ux = vx / len;
    const uy = vy / len;
    const srcSz = this.sizeOf(t.startState);
    const oneSz = this.sizeOf(oneKey);
    const twoSz = this.sizeOf(twoKey);
    const dsz = this.sizeOf('cond:' + t.id);
    const gapStart = this.edgeDist(srcSz.w / 2, srcSz.h / 2, ux, uy);
    const gapEnd =
      len -
      this.edgeDist((oneSz.w + twoSz.w) / 4, (oneSz.h + twoSz.h) / 4, ux, uy);
    const dHalf = this.edgeDist(dsz.w / 2, dsz.h / 2, ux, uy);
    const MINGAP = 30; // breathing room between the ◆ and either box
    let dist = (gapStart + gapEnd) / 2;
    const minDist = gapStart + MINGAP + dHalf;
    const maxDist = gapEnd - MINGAP - dHalf;
    if (dist < minDist) dist = minDist;
    if (maxDist > minDist && dist > maxDist) dist = maxDist;
    return { x: srcC.x + ux * dist, y: srcC.y + uy * dist };
  }

  /**
   * Decision diamonds to render on the canvas — one per guarded transition (endStateCondition).
   * Uses condCenter() so the ◆ sits exactly where the source→◆→outcome edges meet.
   * Clicking it opens the condition/event editor.
   */
  get decisions(): Array<{
    tid: string;
    x: number;
    y: number;
    t: ITransition;
  }> {
    const out: Array<{ tid: string; x: number; y: number; t: ITransition }> =
      [];
    for (const t of this.model.transitions) {
      if (!t.guard || !t.two) continue;
      const c = this.condCenter(t);
      if (!c) continue;
      out.push({ tid: t.id, x: c.x, y: c.y, t });
    }
    return out;
  }

  // ── inspector open handlers ──
  openState(ref: { tid: string; which: 'one' | 'two' } | null): void {
    if (!ref) return;
    this.inspector = {
      mode: 'state',
      tid: ref.tid,
      which: ref.which,
      stateName: this.stateName(this.endStateFor(ref)?.stateCode || ''),
    };
  }
  /** Open the single-action editor for a clicked action chip. */
  openAction(ref: WorkflowOutcomeRef | null, action: IAction): void {
    const es = this.endStateFor(ref);
    if (!ref || !es) return;
    const idx = es.actions.indexOf(action);
    if (idx < 0) return;
    this.inspector = {
      mode: 'action',
      tid: ref.tid,
      which: ref.which,
      actionIdx: idx,
      stateName: this.stateName(es.stateCode),
    };
  }
  /** State view → edit a single action from its list. */
  onInspectorActionEdit(idx: number): void {
    if (this.inspector)
      this.inspector = { ...this.inspector, mode: 'action', actionIdx: idx };
  }
  onInspectorStateEdit(): void {
    if (!this.inspector) return;
    this.inspector = {
      ...this.inspector,
      mode: 'state',
      actionIdx: undefined,
      stateName:
        this.stateName(
          this.endStateFor({
            tid: this.inspector.tid,
            which: this.inspector.which || 'one',
          })?.stateCode || ''
        ) || this.inspector.stateName,
    };
  }
  openEvent(tid: string): void {
    this.inspector = { mode: 'event', tid };
  }
  openCondition(tid: string): void {
    this.inspector = { mode: 'condition', tid };
  }
  closeInspector(): void {
    this.inspector = null;
  }

  // ── highlight the element currently being edited (instead of a black overlay) ──
  get activeStateKey(): string | null {
    if (this.inspector?.mode !== 'state' && this.inspector?.mode !== 'action')
      return null;
    const ref = {
      tid: this.inspector.tid,
      which: this.inspector.which || ('one' as const),
    };
    const occurrence = outcomeNodeKey(ref.tid, ref.which);
    return this.visualGraph.nodes.some(node => node.key === occurrence)
      ? occurrence
      : this.endStateFor(ref)?.stateCode ?? null;
  }
  isActiveEdge(e: FreeEdge): boolean {
    if (!this.inspector || this.inspector.tid !== e.tid) return false;
    if (this.inspector.mode === 'event' || this.inspector.mode === 'condition')
      return true;
    const expected = (this.inspector.which || 'one') === 'one' ? '①' : '②';
    return !e.outcome || e.outcome === expected;
  }

  isStateDimmed(key: string): boolean {
    const focus = this.focusContext();
    return !!focus && !focus.states.has(key);
  }

  isEdgeDimmed(edge: FreeEdge): boolean {
    const focus = this.focusContext();
    return !!focus && !focus.transitions.has(edge.tid);
  }

  private focusContext(): {
    states: Set<string>;
    transitions: Set<string>;
  } | null {
    if (!this.inspector) return null;
    const transition = this.transition(this.inspector.tid);
    if (!transition) return null;
    const states = new Set<string>();
    const transitions = new Set<string>();
    if (
      this.inspector.mode === 'event' ||
      this.inspector.mode === 'condition'
    ) {
      transitions.add(transition.id);
      states.add(transition.startState);
      states.add(this.visualKeyForOutcome(transition, 'one'));
      if (transition.two)
        states.add(this.visualKeyForOutcome(transition, 'two'));
      return { states, transitions };
    }
    const stateCode =
      this.endStateFor({
        tid: this.inspector.tid,
        which: this.inspector.which || 'one',
      })?.stateCode ?? null;
    if (!stateCode) return null;
    const activeKey = this.activeStateKey ?? stateCode;
    states.add(activeKey);
    if (activeKey.startsWith('outcome:')) {
      transitions.add(transition.id);
      states.add(transition.startState);
      return { states, transitions };
    }
    for (const candidate of this.model.transitions) {
      const destinations = [candidate.one.stateCode];
      if (candidate.two) destinations.push(candidate.two.stateCode);
      if (
        candidate.startState !== stateCode &&
        !destinations.includes(stateCode)
      ) {
        continue;
      }
      transitions.add(candidate.id);
      states.add(candidate.startState);
      if (candidate.startState === stateCode) states.add(candidate.startState);
      if (candidate.one.stateCode === stateCode) {
        states.add(this.visualKeyForOutcome(candidate, 'one'));
      }
      if (candidate.two?.stateCode === stateCode) {
        states.add(this.visualKeyForOutcome(candidate, 'two'));
      }
    }
    return { states, transitions };
  }

  outcomeLabel(edge: Pick<FreeEdge, 'outcome' | 'branch'>): string {
    const endState = edge.outcome === '①' ? 'End state 1' : 'End state 2';
    if (edge.branch === 'false') return `False · ${endState}`;
    if (edge.branch === 'true') return `True · ${endState}`;
    return edge.outcome ? endState : '';
  }

  /** Click a warning → centre the offending state on the canvas and flash it. */
  focusWarning(w: Warning): void {
    const code =
      w.stateCode || (w.tid ? this.transition(w.tid)?.startState : undefined);
    if (!code) return;
    this.focusedStateCode = code;
    if (this.focusClearTimer) clearTimeout(this.focusClearTimer);
    this.focusClearTimer = setTimeout(() => {
      this.focusedStateCode = null;
      this.cdr.markForCheck();
    }, 1800);
    // Defer so the flash class is applied before we scroll to the node.
    setTimeout(() => this.scrollNodeIntoView(code), 0);
  }

  /** Centre a state box in the canvas viewport (no page scroll, scale-aware). */
  private scrollNodeIntoView(code: string): void {
    const canvas = this.canvasRef?.nativeElement;
    const node = canvas?.querySelector<HTMLElement>(
      `.free-node[data-state-code="${code}"]`
    );
    if (!canvas || !node) return;
    const n = node.getBoundingClientRect();
    const c = canvas.getBoundingClientRect();
    const left =
      canvas.scrollLeft +
      (n.left - c.left) -
      (canvas.clientWidth - n.width) / 2;
    const top =
      canvas.scrollTop + (n.top - c.top) - (canvas.clientHeight - n.height) / 2;
    canvas.scrollTo({
      left: Math.max(0, left),
      top: Math.max(0, top),
      behavior: 'smooth',
    });
  }
  /**
   * Open the add-event composer for `code`, anchored near the click point.
   * `anchor` (when triggered from a connection point) records the direction so the
   * resulting new state lands on that side of the source.
   */
  openAddEvent(code: string, ev?: MouseEvent, anchor?: AnchorName): void {
    this.composerFrom = code;
    this.composerOpen = true;
    this.composerFirst = false;
    this.composerPaste = false;
    this.lastComposerFrom = code;
    this.lastComposerDir = anchor ? this.dirOf(anchor) : { dx: 0, dy: 1 };
    const W = 340,
      H = 460,
      m = 12;
    if (ev) {
      this.composerPos = {
        x: Math.min(Math.max(m, ev.clientX + 12), window.innerWidth - W - m),
        y: Math.min(Math.max(m, ev.clientY - 16), window.innerHeight - H - m),
      };
    } else {
      this.composerPos = { x: window.innerWidth - W - 32, y: 96 };
    }
  }

  /**
   * Open the composer already switched into edit-before-create mode, prefilled from the
   * copied event, so a right-click → "Paste" lets the user re-point destinations and edit the
   * event before the clone is created (rather than pasting straight onto the source's targets).
   */
  openPasteEvent(code: string, ev?: MouseEvent): void {
    this.openAddEvent(code, ev);
    this.composerPaste = true;
  }

  /**
   * Empty canvas → start the workflow. Every workflow begins at the NEW state with an APPLY
   * event, so the composer opens locked to NEW → APPLY and the user only chooses where it goes.
   */
  startFromScratch(): void {
    this.composerFrom = 'NEW';
    this.composerFirst = true;
    this.lastComposerFrom = 'NEW';
    this.lastComposerDir = { dx: 0, dy: 1 };
    const W = 340;
    this.composerPos = {
      x: Math.max(16, (window.innerWidth - W) / 2),
      y: 120,
    };
    this.composerOpen = true;
  }

  closeComposer(): void {
    this.composerOpen = false;
    this.composerFirst = false;
    this.composerPaste = false;
    this.composerFrom = null;
  }

  get canUndo(): boolean {
    return this.store.canUndo;
  }

  get canRedo(): boolean {
    return this.store.canRedo;
  }

  undo(): boolean {
    if (!this.store.undo()) return false;
    this.afterHistoryChange();
    return true;
  }

  redo(): boolean {
    if (!this.store.redo()) return false;
    this.afterHistoryChange();
    return true;
  }

  @HostListener('document:keydown', ['$event'])
  onHistoryShortcut(event: KeyboardEvent): void {
    if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z') {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (
      target?.closest?.('input, textarea, select, [contenteditable="true"]')
    ) {
      return;
    }
    const changed = event.shiftKey ? this.redo() : this.undo();
    if (changed) event.preventDefault();
  }

  /**
   * ⌘C / Ctrl+C copies the event currently open in the inspector; ⌘V / Ctrl+V pastes the copied
   * event onto the open state (opening the edit-before-create composer). Text selections inside
   * inputs keep the native clipboard behaviour.
   */
  @HostListener('document:keydown', ['$event'])
  onClipboardShortcut(event: KeyboardEvent): void {
    if (!(event.metaKey || event.ctrlKey)) return;
    const key = event.key.toLowerCase();
    if (key !== 'c' && key !== 'v') return;
    const target = event.target as HTMLElement | null;
    if (
      target?.closest?.('input, textarea, select, [contenteditable="true"]')
    ) {
      return;
    }
    const handled =
      key === 'c' ? this.copyActiveEvent() : this.pasteIntoActiveState();
    if (handled) event.preventDefault();
  }

  /** Copy the event currently focused in the inspector to the clipboard. */
  private copyActiveEvent(): boolean {
    const tid = this.inspector?.tid;
    if (!tid || !this.store.copyEvent(tid)) return false;
    this.onEventCopied(this.transition(tid)?.event || '');
    return true;
  }

  /** Paste the copied event onto the state open in the inspector (edit-before-create). */
  private pasteIntoActiveState(): boolean {
    if (!this.store.clipboardEvent) return false;
    const key = this.activeStateKey;
    const code = key
      ? this.visualGraph.nodes.find(n => n.key === key)?.stateCode
      : undefined;
    if (!code) return false;
    this.openPasteEvent(code);
    this.cdr.detectChanges();
    return true;
  }

  private afterHistoryChange(): void {
    this.closeInspector();
    this.closeComposer();
    this.freePos = this.collectSavedPositions();
    this.placeMissingPositions();
    this.freeEdgeAnchors = {};
    this.recomputeFreeSize();
    this.scheduleFreeRecompute();
  }

  private dirOf(a: AnchorName): { dx: number; dy: number } {
    if (a[0] === 't') return { dx: 0, dy: -1 };
    if (a[0] === 'b') return { dx: 0, dy: 1 };
    if (a[0] === 'l') return { dx: -1, dy: 0 };
    return { dx: 1, dy: 0 };
  }

  // ── drag an action chip from one state onto another to duplicate it ──
  draggingAction: IAction | null = null;
  private draggingFromKey: string | null = null;
  onChipDragStart(a: IAction, fromKey: string): void {
    this.draggingAction = a;
    this.draggingFromKey = fromKey;
  }
  onChipDragEnd(): void {
    this.draggingAction = null;
    this.draggingFromKey = null;
  }
  onChipDropTo(
    ref: { tid: string; which: 'one' | 'two' } | null,
    targetKey: string
  ): void {
    if (!this.draggingAction || !ref) return;
    // Exact visual occurrences can share a runtime state code but own different actions.
    if (targetKey === this.draggingFromKey) return;
    this.store.addActionCopy(ref.tid, ref.which, this.draggingAction);
  }

  // ── copy an event to the clipboard → paste (⌘V) into a new event (see add-event composer) ──
  /** Transient toast (e.g. "event copied", paste hints). */
  copyNotice = '';
  /** Show a hint after an event is copied; the user pastes it via ⌘V in a state's "+ add event". */
  onEventCopied(eventName: string): void {
    this.copyNotice = `Event "${eventName}" copied — open a state's "＋ add event" and press ⌘V, or right-click a state → Paste.`;
    setTimeout(() => {
      this.copyNotice = '';
      this.cdr.markForCheck();
    }, 6000);
    this.cdr.markForCheck();
  }

  // ── right-click context menu (configure / copy an event; paste / add on a state) ──
  ctxMenu: {
    x: number;
    y: number;
    items: Array<{ label: string; run: () => void }>;
  } | null = null;

  /** Right-click an event label → Configure / Copy. */
  onEventContext(tid: string, e: MouseEvent): void {
    e.preventDefault();
    e.stopPropagation();
    this.ctxMenu = {
      x: e.clientX,
      y: e.clientY,
      items: [
        { label: '⚙ Configure event', run: () => this.openEvent(tid) },
        {
          label: '⧉ Copy event',
          run: () => {
            if (this.store.copyEvent(tid))
              this.onEventCopied(this.transition(tid)?.event || '');
          },
        },
        { label: '🗑 Delete event', run: () => this.deleteEvent(tid) },
      ],
    };
    this.cdr.detectChanges(); // render the menu synchronously (robust against zone timing)
  }

  /** Right-click a single action chip → Copy it (and Paste the clipboard action onto it). */
  onActionContext(
    payload: { action: IAction; event: MouseEvent },
    ref: WorkflowOutcomeRef | null
  ): void {
    const e = payload.event;
    const items: Array<{ label: string; run: () => void }> = [
      {
        label: `⧉ Copy action “${payload.action.actionType}”`,
        run: () => this.onActionCopied(payload.action),
      },
    ];
    const clip = this.store.clipboardAction;
    if (clip && ref) {
      const target = ref;
      items.push({
        label: `📋 Paste action “${clip.actionType}” here`,
        run: () => this.pasteActionOntoRef(target, clip.actionType),
      });
    }
    this.ctxMenu = { x: e.clientX, y: e.clientY, items };
    this.cdr.detectChanges(); // render the menu synchronously (robust against zone timing)
  }

  /** Copy a single action to the clipboard and hint how to paste it. */
  onActionCopied(action: IAction): void {
    this.store.copyAction(action);
    this.copyNotice = `Action "${action.actionType}" copied — right-click a state (or an action on it) → Paste action.`;
    setTimeout(() => {
      this.copyNotice = '';
      this.cdr.markForCheck();
    }, 6000);
    this.cdr.markForCheck();
  }

  /** Paste the clipboard action onto an outcome, with a confirming toast. */
  private pasteActionOntoRef(
    ref: WorkflowOutcomeRef,
    actionType: string
  ): void {
    if (this.store.pasteActionOnto(ref.tid, ref.which))
      this.notice(`Action "${actionType}" pasted.`);
  }

  /** Right-click a state node → Paste copied event (if any) / Add event. */
  onStateContext(node: WorkflowVisualNode, e: MouseEvent): void {
    e.preventDefault();
    e.stopPropagation();
    const code = node.stateCode;
    const items: Array<{ label: string; run: () => void }> = [
      ...(node.ref
        ? [
            {
              label: '⚙ Configure outcome',
              run: () => this.openState(node.ref),
            },
          ]
        : []),
      {
        label: '＋ Add event from here',
        run: () => this.openAddEvent(code, e),
      },
    ];
    const clip = this.store.clipboardEvent;
    if (clip)
      items.unshift({
        label: `📋 Paste event "${clip}"…`,
        run: () => this.openPasteEvent(code, e),
      });
    // Paste a single copied action onto this state's outcome (keeps the state's own actions).
    const clipAction = this.store.clipboardAction;
    if (clipAction && node.ref) {
      const ref = node.ref;
      items.push({
        label: `📋 Paste action “${clipAction.actionType}” here`,
        run: () => this.pasteActionOntoRef(ref, clipAction.actionType),
      });
    }
    // Copy the event (and all its outcomes) straight from the state it lands on, so users
    // don't have to hunt for the event label to reach "Copy". Any node with an incoming event
    // carries a ref (canonical nodes point at their first incoming event; occurrence cards at
    // their own). ⌘C copies the same event when this state is open in the inspector.
    if (node.ref) {
      const tid = node.ref.tid;
      const event = this.transition(tid)?.event || 'event';
      items.push({
        label: `⧉ Copy incoming event “${event}” (⌘C)`,
        run: () => {
          if (this.store.copyEvent(tid)) this.onEventCopied(event);
        },
      });
    }
    if (node.key.startsWith('outcome:') && node.ref) {
      const event = this.transition(node.ref.tid)?.event || 'event';
      items.push({
        label: `🗑 Delete incoming event “${event}”`,
        run: () => this.deleteEvent(node.ref?.tid || ''),
      });
    } else {
      items.push({
        label: '🗑 Delete state',
        run: () => this.deleteStateNode(code),
      });
    }
    this.ctxMenu = { x: e.clientX, y: e.clientY, items };
    this.cdr.detectChanges(); // render the menu synchronously (robust against zone timing)
  }

  // ── delete (event / state) — always behind a confirmation that previews the impact ──
  /** What a pending delete will remove, shown in the confirmation popup. null = closed. */
  confirmDelete: {
    kind: 'event' | 'state' | 'action';
    id: string; // transition id (event) or state code (state)
    which?: 'one' | 'two';
    actionIndex?: number;
    actionType?: string;
    heading: string;
    events: string[]; // event codes that will be removed
    states: string[]; // state codes that will be removed (incl. orphaned downstream states)
  } | null = null;

  /** Compute exactly which events and states disappear once `removeTids` are dropped. */
  private deletionImpact(removeTids: Set<string>): {
    events: string[];
    states: string[];
  } {
    const surviving = new Set<string>();
    for (const t of this.model.transitions) {
      if (removeTids.has(t.id)) continue;
      surviving.add(t.startState);
      if (t.one?.stateCode) surviving.add(t.one.stateCode);
      if (t.two?.stateCode) surviving.add(t.two.stateCode);
    }
    const states = this.model.states
      .map(s => s.code)
      .filter(code => !surviving.has(code));
    const events = [
      ...new Set(
        this.model.transitions
          .filter(t => removeTids.has(t.id))
          .map(t => t.event)
      ),
    ];
    return { events, states };
  }

  /** Ask before removing an event (transition) and any end state it leads to. */
  deleteEvent(tid: string): void {
    const t = this.transition(tid);
    if (!t) return;
    const impact = this.deletionImpact(new Set([tid]));
    this.confirmDelete = {
      kind: 'event',
      id: tid,
      heading: `Delete event “${t.event || 'event'}”?`,
      events: impact.events,
      states: impact.states,
    };
    this.cdr.detectChanges();
  }

  /** Ask before removing a state and every event into or out of it. */
  deleteStateNode(code: string): void {
    const removeTids = new Set(
      this.model.transitions
        .filter(
          t =>
            t.startState === code ||
            t.one?.stateCode === code ||
            t.two?.stateCode === code
        )
        .map(t => t.id)
    );
    if (!removeTids.size) return;
    const impact = this.deletionImpact(removeTids);
    this.confirmDelete = {
      kind: 'state',
      id: code,
      heading: `Delete state “${this.stateName(code) || code}”?`,
      events: impact.events,
      states: impact.states,
    };
    this.cdr.detectChanges();
  }

  /** Ask before removing an action from a state. */
  deleteAction(req: InspectorActionDeleteRequest): void {
    this.confirmDelete = {
      kind: 'action',
      id: req.tid,
      which: req.which,
      actionIndex: req.index,
      actionType: req.actionType,
      heading: `Delete action “${req.actionType || 'action'}”?`,
      events: [],
      states: [],
    };
    this.cdr.detectChanges();
  }

  /** User confirmed the popup → perform the deletion. */
  confirmDeleteRun(): void {
    const c = this.confirmDelete;
    if (!c) return;
    this.confirmDelete = null;
    if (c.kind === 'action') {
      if (c.which && c.actionIndex != null) {
        this.store.removeAction(c.id, c.which, c.actionIndex);
        if (
          this.inspector?.mode === 'action' &&
          this.inspector.tid === c.id &&
          this.inspector.which === c.which &&
          this.inspector.actionIdx === c.actionIndex
        ) {
          this.onInspectorStateEdit();
        }
        this.notice(`Deleted action “${c.actionType || 'action'}”.`);
      }
      return;
    }
    if (this.inspector?.tid === c.id || c.kind === 'state')
      this.closeInspector();
    if (c.kind === 'event') {
      this.store.deleteTransition(c.id);
      this.notice(`Deleted event “${c.events[0] || ''}”.`);
    } else {
      this.store.deleteState(c.id);
      this.notice(`Deleted state and ${c.events.length} connected event(s).`);
    }
  }

  confirmDeleteCancel(): void {
    this.confirmDelete = null;
  }

  // ── move (re-anchor an event's arrow onto another state) — behind a confirmation ──
  /**
   * A pending move, previewed before it is applied. `end` says which endpoint was dragged:
   *   'dst' → re-point the outcome (its actions travel with it by default; the author can
   *           uncheck `carryActions` to land on a bare state), 'src' → re-anchor where the
   *           event leaves from (the event and its actions always travel with it).
   */
  confirmMove: {
    tid: string;
    end: 'src' | 'dst';
    which?: 'one' | 'two';
    event: string;
    fromCode: string;
    toCode: string;
    heading: string;
    /** Action types configured on the re-pointed outcome (empty for a source move). */
    outcomeActions: string[];
    /** Whether the outcome's actions travel to the new destination (dst moves only, default on). */
    carryActions: boolean;
    /** State codes that disappear from the diagram once the move lands. */
    disappears: string[];
    /**
     * Set when the move leaves the old destination unreachable while it still has outgoing
     * events — offer to re-point those onward events so its sub-flow isn't stranded.
     */
    cascade: { code: string; events: string[] } | null;
    /** User's choice for the cascade re-point (default on). */
    cascadeRepoint: boolean;
  } | null = null;

  /**
   * A move that was refused because the target already owns this event (moving there would
   * override it). Surfaced as a dialog so the reason is explicit — nothing is changed.
   */
  blockedMove: {
    heading: string;
    message: string;
    event: string;
    toLabel: string;
  } | null = null;

  /** Refuse a move with an explaining dialog (the target keeps all its own config). */
  private blockMove(event: string, toLabel: string, message: string): void {
    this.blockedMove = {
      heading: `Can’t move “${event}” to “${toLabel}”`,
      message,
      event,
      toLabel,
    };
    this.cdr.detectChanges(); // render synchronously (robust against zone timing)
  }

  dismissBlockedMove(): void {
    this.blockedMove = null;
  }

  /** Which node (if any) sits under a point — the drop target while dragging an endpoint. */
  private nodeKeyAt(
    pt: { x: number; y: number },
    excludeKey: string
  ): string | null {
    for (const node of this.visualGraph.nodes) {
      if (node.key === excludeKey) continue;
      const b = this.boxCenterSize(node.key);
      if (!b) continue;
      if (
        pt.x >= b.cx - b.w / 2 &&
        pt.x <= b.cx + b.w / 2 &&
        pt.y >= b.cy - b.h / 2 &&
        pt.y <= b.cy + b.h / 2
      )
        return node.key;
    }
    return null;
  }

  /**
   * Work out what a move would change WITHOUT mutating: which actions get dropped, which states
   * disappear from the diagram, and whether the old destination is left stranded with onward
   * events (the cascade case). Pure — simulates the resulting transition list.
   */
  private moveImpact(
    edge: FreeEdge,
    end: 'src' | 'dst',
    toCode: string
  ): {
    outcomeActions: string[];
    disappears: string[];
    cascade: { code: string; events: string[] } | null;
  } {
    const which: 'one' | 'two' = edge.outcome === '②' ? 'two' : 'one';
    const sim: ITransition[] = this.model.transitions.map(t => {
      if (t.id !== edge.tid) return t;
      if (end === 'dst') {
        if (which === 'two') {
          return t.two ? { ...t, two: { ...t.two, stateCode: toCode } } : t;
        }
        return { ...t, one: { ...t.one, stateCode: toCode } };
      }
      return { ...t, startState: toCode };
    });
    const present = (code: string): boolean =>
      sim.some(
        t =>
          t.startState === code ||
          t.one.stateCode === code ||
          (t.two != null && t.two.stateCode === code)
      );
    const disappears = this.model.states
      .map(s => s.code)
      .filter(code => !present(code));
    const source = this.transition(edge.tid);
    const outcomeActions =
      end === 'dst'
        ? (which === 'two' ? source?.two : source?.one)?.actions.map(
            a => a.actionType
          ) ?? []
        : [];
    let cascade: { code: string; events: string[] } | null = null;
    if (end === 'dst') {
      const oldCode = edge.dstCode;
      const stillReached = sim.some(
        t =>
          t.one.stateCode === oldCode ||
          (t.two != null && t.two.stateCode === oldCode)
      );
      const onward = sim.filter(t => t.startState === oldCode);
      if (!stillReached && onward.length && !disappears.includes(oldCode)) {
        cascade = {
          code: oldCode,
          events: [...new Set(onward.map(t => t.event))],
        };
      }
    }
    return { outcomeActions, disappears, cascade };
  }

  /** A drop onto a different node was released → stage the move behind a confirmation. */
  private beginMove(edge: FreeEdge, end: 'src' | 'dst', dropKey: string): void {
    const node = this.visualGraph.nodes.find(n => n.key === dropKey);
    const t = this.transition(edge.tid);
    if (!node || !t) return;
    const toCode = node.stateCode;
    const toLabel = this.stateName(toCode) || toCode;
    if (end === 'dst') {
      if (toCode === edge.dstCode) return; // no-op: released on the same destination
      const which: 'one' | 'two' = edge.outcome === '②' ? 'two' : 'one';
      // Block a move that would land this event on a state it already reaches: the other
      // outcome of the same conditional transition already ends there. Converging both
      // outcomes on one state would override that path — leave the target untouched.
      const other = which === 'two' ? t.one : t.two;
      if (other && other.stateCode === toCode) {
        this.blockMove(
          t.event,
          toLabel,
          `“${t.event}” already leads to “${toLabel}” through its other outcome. ` +
            `Moving it here would converge both outcomes on one state and override that path.`
        );
        return;
      }
      const impact = this.moveImpact(edge, 'dst', toCode);
      this.confirmMove = {
        tid: edge.tid,
        end: 'dst',
        which,
        event: t.event,
        fromCode: edge.dstCode,
        toCode,
        heading: `Move “${t.event}” to “${toLabel}”?`,
        outcomeActions: impact.outcomeActions,
        carryActions: true,
        disappears: impact.disappears,
        cascade: impact.cascade,
        cascadeRepoint: true,
      };
    } else {
      if (toCode === edge.srcCode) return; // no-op: released on the same source
      const clash = this.model.transitions.some(
        x => x.id !== edge.tid && x.startState === toCode && x.event === t.event
      );
      if (clash) {
        this.blockMove(
          t.event,
          toLabel,
          `“${toLabel}” already has a “${t.event}” event leaving it. ` +
            `Moving this one there would override the existing event.`
        );
        return;
      }
      const impact = this.moveImpact(edge, 'src', toCode);
      this.confirmMove = {
        tid: edge.tid,
        end: 'src',
        event: t.event,
        fromCode: edge.srcCode,
        toCode,
        heading: `Move the source of “${t.event}” to “${toLabel}”?`,
        outcomeActions: [],
        carryActions: true,
        disappears: impact.disappears,
        cascade: null,
        cascadeRepoint: false,
      };
    }
    this.cdr.detectChanges();
  }

  /** User confirmed → apply the staged move (and any cascade re-point). */
  confirmMoveRun(): void {
    const c = this.confirmMove;
    if (!c) return;
    this.confirmMove = null;
    if (c.end === 'dst' && c.which) {
      this.store.setEndStateCode(c.tid, c.which, c.toCode, c.carryActions);
      if (c.cascade && c.cascadeRepoint) {
        // Re-read after the re-point: repoint the stranded state's onward events onto the
        // new destination so its sub-flow reconnects, then open the first for editing.
        const onward = this.model.transitions.filter(
          t => t.startState === c.cascade?.code
        );
        let firstTid: string | null = null;
        let repointed = 0;
        for (const t of onward) {
          if (this.store.setStartState(t.id, c.toCode)) {
            repointed++;
            firstTid = firstTid ?? t.id;
          }
        }
        this.notice(
          `Moved “${c.event}” and re-pointed ${repointed} event(s) from “${c.cascade.code}”.`
        );
        if (firstTid) this.openEvent(firstTid);
      } else {
        this.notice(`Moved “${c.event}” to “${c.toCode}”.`);
      }
    } else if (c.end === 'src') {
      const ok = this.store.setStartState(c.tid, c.toCode);
      this.notice(
        ok
          ? `Moved the source of “${c.event}” to “${c.toCode}”.`
          : `Couldn’t move: “${c.toCode}” already has “${c.event}”.`
      );
    }
  }

  confirmMoveCancel(): void {
    this.confirmMove = null;
  }

  /** Transient toast helper shared by the destructive (delete) actions. */
  private notice(msg: string): void {
    this.copyNotice = msg;
    setTimeout(() => {
      this.copyNotice = '';
      this.cdr.markForCheck();
    }, 4000);
    this.cdr.markForCheck();
  }

  runCtx(item: { run: () => void }): void {
    item.run();
    this.ctxMenu = null;
  }
  closeCtxMenu(): void {
    this.ctxMenu = null;
  }
  @HostListener('document:keydown.escape') onEscape(): void {
    if (this.blockedMove) this.dismissBlockedMove();
    else if (this.confirmMove) this.confirmMoveCancel();
    else if (this.confirmDelete) this.confirmDeleteCancel();
    else if (this.ctxMenu) this.closeCtxMenu();
  }

  // ── free mode: per-state representative endState / ref ──
  endStateForCode(code: string): IEndState | null {
    for (const t of this.model.transitions) {
      if (t.one.stateCode === code) return t.one;
      if (t.two && t.two.stateCode === code) return t.two;
    }
    return null;
  }
  private visualKeyForOutcome(
    transition: ITransition,
    which: 'one' | 'two'
  ): string {
    const key = outcomeNodeKey(transition.id, which);
    return this.visualGraph.nodes.some(node => node.key === key)
      ? key
      : which === 'one'
      ? transition.one.stateCode
      : transition.two?.stateCode ?? transition.one.stateCode;
  }
  refForCode(code: string): { tid: string; which: 'one' | 'two' } | null {
    for (const t of this.model.transitions) {
      if (t.one.stateCode === code) return { tid: t.id, which: 'one' };
      if (t.two && t.two.stateCode === code) return { tid: t.id, which: 'two' };
    }
    return null;
  }
  toggleFreeMode(): void {
    this.freeMode ? this.exitFreeMode() : this.enterFreeMode();
  }
  exitFreeMode(): void {
    this.freeMode = false;
    this.sizingDirty = true;
  }
  enterFreeMode(): void {
    this.freeMode = true;
    this.seeded = false;
    this.sizingDirty = true;
  }

  /** Positions already saved on the flow's endState nodes (raw.position), keyed by state code. */
  private collectSavedPositions(): Record<string, { x: number; y: number }> {
    const pos: Record<string, { x: number; y: number }> = {};
    const occurrencePositions = new Map<
      string,
      Array<{ key: string; x: number; y: number }>
    >();
    for (const node of this.visualGraph.nodes) {
      const es = this.endStateForNode(node);
      const p = (es?.raw as { position?: { x: number; y: number } } | undefined)
        ?.position;
      if (!p || typeof p.x !== 'number' || typeof p.y !== 'number') continue;
      if (node.key.startsWith('outcome:')) {
        const entries = occurrencePositions.get(node.stateCode) ?? [];
        entries.push({ key: node.key, x: p.x, y: p.y });
        occurrencePositions.set(node.stateCode, entries);
      } else if (!pos[node.key]) {
        pos[node.key] = { x: p.x, y: p.y };
      }
    }
    occurrencePositions.forEach(entries => {
      const distinct = new Set(entries.map(entry => `${entry.x}:${entry.y}`));
      // The previous code-based editor persisted one shared coordinate into every
      // repeated end state. Ignore that collapsed legacy position and re-place them.
      if (entries.length > 1 && distinct.size === 1) return;
      entries.forEach(entry => {
        pos[entry.key] = { x: entry.x, y: entry.y };
      });
    });
    return pos;
  }

  /**
   * Drop positions for removed visual occurrences and migrate one useful position
   * when a terminal state expands or collapses after an event edit.
   */
  private reconcileVisualPositions(previous: WorkflowVisualGraph): void {
    const next: Record<string, { x: number; y: number }> = {};
    this.visualGraph.nodes.forEach(node => {
      const same = this.freePos[node.key];
      if (same) {
        next[node.key] = same;
        return;
      }
      const previousNode = previous.nodes.find(
        candidate =>
          candidate.stateCode === node.stateCode && this.freePos[candidate.key]
      );
      // Preserve a single representative coordinate. Other newly-expanded
      // occurrences are placed beside their own incoming source below.
      if (
        previousNode &&
        (!node.key.startsWith('outcome:') || node.occurrenceIndex === 1)
      ) {
        next[node.key] = this.freePos[previousNode.key];
      }
    });
    this.freePos = next;
    const live = new Set(this.visualGraph.nodes.map(node => node.key));
    this.nodeSizes = Object.fromEntries(
      Object.entries(this.nodeSizes).filter(
        ([key]) => live.has(key) || key.startsWith('cond:')
      )
    );
  }

  /**
   * First render in free mode. KEEP positions the flow already carries (user-set / BE-stored)
   * so switching JSON↔Design — or reopening a saved flow — never reformats the user's layout.
   * Only a fresh flow with NO saved positions is auto-tidied; unpositioned states (e.g. a start
   * state, which never carries a position) are placed relative to their neighbours.
   */
  private seedFreePositions(): void {
    this.freeEdgeAnchors = {}; // fresh workflow → drop stale anchor choices
    const saved = this.collectSavedPositions();
    if (Object.keys(saved).length > 0) {
      this.freePos = { ...saved };
      this.placeMissingPositions(); // start/new states without a saved position
      this.pendingTidy = false; // honour saved positions — do NOT re-tidy
      this.requestInitialViewportFrame();
    } else {
      this.freePos = this.tidyLayout();
      this.pendingTidy = false;
      setTimeout(() => this.applyAutoLayout(false), 0);
    }
    this.recomputeFreeSize();
    this.scheduleFreeRecompute();
  }

  /** "Tidy up" — re-arrange every box into a clean, well-spaced flow. Stays draggable. */
  tidyUp(dir?: 'vertical' | 'horizontal'): void {
    if (dir) this.tidyDir = dir;
    this.applyAutoLayout(true);
  }

  private applyAutoLayout(persist: boolean): void {
    const request = ++this.autoLayoutRequest;
    const model = this.model;
    this.layoutBusy = true;
    this.cdr.markForCheck();
    void autoLayoutWorkflow(model, this.tidyDir, stateCode => {
      const size = this.sizeOf(stateCode);
      return { width: size.w, height: size.h };
    })
      .then(positions => {
        if (request !== this.autoLayoutRequest) return;
        this.freePos = positions;
        this.freeEdgeAnchors = {};
        this.recomputeFreeSize();
        if (persist) {
          const canonical: Record<string, { x: number; y: number }> = {};
          const outcomes: Array<{
            tid: string;
            which: 'one' | 'two';
            position: { x: number; y: number };
          }> = [];
          this.visualGraph.nodes.forEach(node => {
            const position = positions[node.key];
            if (!position) return;
            if (node.key.startsWith('outcome:') && node.ref) {
              outcomes.push({ ...node.ref, position });
            } else {
              canonical[node.stateCode] = position;
            }
          });
          this.store.setPositions(canonical, outcomes);
        }
        if (!persist) this.requestInitialViewportFrame();
        this.scheduleFreeRecompute();
      })
      .catch(() => {
        if (request === this.autoLayoutRequest) {
          this.notice('Unable to tidy this workflow automatically.');
        }
      })
      .finally(() => {
        if (request !== this.autoLayoutRequest) return;
        this.layoutBusy = false;
        this.cdr.markForCheck();
      });
  }

  private requestInitialViewportFrame(): void {
    if (this.initialViewportFramed) return;
    this.initialViewportFramePending = true;
    this.sizingDirty = true;
    this.cdr.markForCheck();
  }

  /**
   * Frame the workflow on first render so the author sees where to start: fit the whole graph
   * when it fits the viewport, otherwise centre the first event's source (APPLY / NEW) near the
   * top so the flow reads downward from there.
   */
  private frameInitialView(): void {
    // On load, zoom-to-fit the whole workflow so the entry is obvious and a side branch
    // (e.g. an ANY → CANCEL escape hatch) can never be mistaken for where the flow starts.
    this.fitToView();
  }

  /** Render-space bounding box of every placed node, or null when nothing is placed. */
  private contentBounds(): {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
  } | null {
    const keys = Object.keys(this.freePos);
    if (!keys.length) return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const key of keys) {
      const sz = this.nodeSizes[key] ?? { w: 258, h: this.estH(key) };
      const x = this.renderX(key);
      const y = this.renderY(key);
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + sz.w);
      maxY = Math.max(maxY, y + sz.h);
    }
    return { minX, minY, maxX, maxY };
  }

  /** Scroll so the whole workflow's bounding box sits centred in the viewport at the current zoom. */
  private centerContent(): void {
    const el = this.canvasRef?.nativeElement;
    const b = this.contentBounds();
    if (!el || !b) return;
    const w = b.maxX - b.minX;
    const h = b.maxY - b.minY;
    el.scrollLeft = Math.max(
      0,
      (b.minX + w / 2) * this.scale - el.clientWidth / 2
    );
    el.scrollTop = Math.max(
      0,
      (b.minY + h / 2) * this.scale - el.clientHeight / 2
    );
  }

  /**
   * "Fit" — zoom the whole workflow to fit the viewport (never past 100%) and centre it. The
   * reliable "show me everything" control, and the on-load default.
   */
  fitToView(): void {
    const el = this.canvasRef?.nativeElement;
    const b = this.contentBounds();
    if (!el || !b) return;
    const pad = 56;
    const w = b.maxX - b.minX;
    const h = b.maxY - b.minY;
    if (w <= 0 || h <= 0) return;
    const cw = Math.max(1, el.clientWidth - pad * 2);
    const ch = Math.max(1, el.clientHeight - pad * 2);
    const fit = Math.min(cw / w, ch / h);
    // Never zoom past 100% on fit, but allow well below the interactive floor so
    // a large workflow can still be framed whole rather than bottoming out.
    this.scale = Math.min(1, Math.max(this.MIN_SCALE, +fit.toFixed(3)));
    this.sizingDirty = true;
    // Re-measure + resize the scroll area synchronously so centring uses the new scale.
    this.ngAfterViewChecked();
    this.centerContent();
    this.cdr.markForCheck();
  }

  /**
   * "Recenter" — bring the workflow's entry (the state the first APPLY event leaves from) back
   * into view at the current zoom, without changing the zoom level.
   */
  recenter(): void {
    const code = this.initialStateCode();
    if (code && this.freePos[code]) this.scrollCodeIntoView(code);
    else this.centerContent();
    this.cdr.markForCheck();
  }

  private initialStateCode(): string | null {
    // The mandatory first event is APPLY — frame the state it leaves from.
    const apply = this.model.transitions.find(t => t.event === 'APPLY');
    if (
      apply &&
      this.model.states.some(state => state.code === apply.startState)
    )
      return apply.startState;
    if (this.model.states.some(state => state.code === 'NEW')) return 'NEW';

    const destinations = new Set<string>();
    for (const transition of this.model.transitions) {
      destinations.add(transition.one.stateCode);
      if (transition.two) destinations.add(transition.two.stateCode);
    }
    return (
      this.model.states.find(
        state => state.code !== 'ANY' && !destinations.has(state.code)
      )?.code ??
      this.model.states.find(state => !destinations.has(state.code))?.code ??
      this.model.transitions[0]?.startState ??
      this.model.states[0]?.code ??
      null
    );
  }

  /** Position of the first outgoing target of `code` that already has one (else null). */
  private firstPositionedTarget(code: string): { x: number; y: number } | null {
    for (const t of this.model.transitions) {
      if (t.startState !== code) continue;
      const oneKey = this.visualKeyForOutcome(t, 'one');
      if (this.freePos[oneKey]) return this.freePos[oneKey];
      if (t.two) {
        const twoKey = this.visualKeyForOutcome(t, 'two');
        if (this.freePos[twoKey]) return this.freePos[twoKey];
      }
    }
    return null;
  }

  /**
   * Place new states (no position yet) next to the state they were added from,
   * in the direction of the connection point used (falls back to "below"). Root states
   * (no incoming) are placed just before their first positioned target.
   */
  private placeMissingPositions(): void {
    const W = 258;
    let changed = false;
    for (const s of this.visualGraph.nodes) {
      if (this.freePos[s.key]) continue;
      const incoming = this.visualGraph.edges.find(
        edge => edge.targetKey === s.key
      );
      const srcCode = incoming?.sourceKey ?? null;
      const src = srcCode ? this.freePos[srcCode] : null;
      let pos = { x: 40, y: 40 };
      if (src && srcCode) {
        const dir =
          srcCode === this.lastComposerFrom
            ? this.lastComposerDir
            : { dx: 0, dy: 1 };
        const srcH = this.estH(srcCode);
        let dy = 0;
        if (dir.dy > 0) dy = srcH + 70;
        else if (dir.dy < 0) dy = -(this.estH(s.key) + 70);
        pos = {
          x: Math.max(
            0,
            src.x + dir.dx * (W + 80) + (s.occurrenceIndex - 1) * 18
          ),
          y: Math.max(0, src.y + dy),
        };
      } else {
        // root state (no incoming): place it just before its first positioned outgoing
        // target so it doesn't pile up at the origin with other roots.
        const tgtPos = this.firstPositionedTarget(s.stateCode);
        if (tgtPos) {
          pos =
            this.tidyDir === 'horizontal'
              ? { x: tgtPos.x - (W + 130), y: tgtPos.y }
              : { x: tgtPos.x, y: tgtPos.y - (this.estH(s.key) + 130) };
        }
      }
      this.freePos = { ...this.freePos, [s.key]: pos };
      this.justAddedCode = s.key; // newest unplaced state → scroll it into view after render
      changed = true;
    }
    // NOTE: placement is local only — persisting here (inside the model subscription)
    // would re-enter the store and double-emit. It sticks once the user drags/tidies.
    if (changed) this.recomputeFreeSize();
  }

  /** Layered top-down layout (BFS levels from start states) with comfortable gaps. */
  private tidyLayout(): Record<string, { x: number; y: number }> {
    const codes = this.visualGraph.nodes.map(s => s.key);
    const adj: Record<string, string[]> = {};
    const indeg: Record<string, number> = {};
    codes.forEach(c => {
      adj[c] = [];
      indeg[c] = 0;
    });
    for (const edge of this.visualGraph.edges) {
      const d = edge.targetKey;
      if (
        d !== edge.sourceKey &&
        adj[edge.sourceKey] &&
        !adj[edge.sourceKey].includes(d)
      ) {
        adj[edge.sourceKey].push(d);
        indeg[d]++;
      }
    }
    let roots = codes.filter(c => indeg[c] === 0);
    if (!roots.length && codes.length) roots = [codes[0]];
    const level: Record<string, number> = {};
    const q = [...roots];
    roots.forEach(r => (level[r] = 0));
    while (q.length) {
      const c = q.shift();
      if (c === undefined) break;
      for (const d of adj[c])
        if (level[d] === undefined) {
          level[d] = level[c] + 1;
          q.push(d);
        }
    }
    codes.forEach(c => {
      if (level[c] === undefined) level[c] = 0;
    });
    const byLevel: Record<number, string[]> = {};
    codes.forEach(c => {
      (byLevel[level[c]] ??= []).push(c);
    });
    const order = Object.keys(byLevel)
      .map(Number)
      .sort((a, b) => a - b);
    const W = 258,
      hGap = 130,
      vGap = 120,
      start = 40;
    // Levels that feed a ◆ decision need extra room so the diamond + branch labels sit clearly
    // between source and outcomes. Horizontal needs much more (the card lays out side-by-side).
    const DECISION_GAP = this.tidyDir === 'horizontal' ? 480 : 150;
    const decAfter: Record<number, boolean> = {};
    for (const t of this.model.transitions) {
      if (t.guard && level[t.startState] !== undefined) {
        decAfter[level[t.startState]] = true;
      }
    }
    const pos: Record<string, { x: number; y: number }> = {};

    if (this.tidyDir === 'horizontal') {
      // levels flow LEFT→RIGHT; siblings stack vertically (better for wide graphs)
      const tallest = Math.max(
        ...order.map(L =>
          byLevel[L].reduce((s, c) => s + this.estH(c) + vGap, -vGap)
        ),
        120
      );
      let x = start;
      for (const L of order) {
        const col = byLevel[L];
        const colH = col.reduce((s, c) => s + this.estH(c) + vGap, -vGap);
        let y = start + Math.max(0, (tallest - colH) / 2);
        for (const c of col) {
          pos[c] = { x: Math.round(x), y: Math.round(y) };
          y += this.estH(c) + vGap;
        }
        x += W + hGap + (decAfter[L] ? DECISION_GAP : 0);
      }
      return pos;
    }

    // vertical (default): levels flow TOP→DOWN; siblings spread horizontally
    const widest = Math.max(
      ...order.map(L => byLevel[L].length * W + (byLevel[L].length - 1) * hGap),
      W
    );
    let y = start;
    for (const L of order) {
      const row = byLevel[L];
      const rowW = row.length * W + (row.length - 1) * hGap;
      let x = start + Math.max(0, (widest - rowW) / 2);
      let maxH = 0;
      for (const c of row) {
        pos[c] = { x: Math.round(x), y: Math.round(y) };
        x += W + hGap;
        maxH = Math.max(maxH, this.estH(c));
      }
      y += maxH + vGap + (decAfter[L] ? DECISION_GAP : 0);
    }
    return pos;
  }
  private estH(key: string): number {
    const s = this.nodeSizes[key];
    if (s) return s.h;
    const node = this.visualGraph.nodes.find(
      candidate => candidate.key === key
    );
    const es = node ? this.endStateForNode(node) : this.endStateForCode(key);
    return 96 + (es ? es.actions.length * 24 : 0) + (es?.nextEvent ? 20 : 0);
  }

  /** Defer node measuring + edge routing to the next tick (after the free-layer renders). */
  private scheduleFreeRecompute(): void {
    setTimeout(() => {
      if (!this.freeMode) return;
      this.measureNodes();
      if (this.pendingTidy) {
        this.pendingTidy = false;
        this.freePos = this.tidyLayout();
        this.recomputeFreeSize();
      }
      this.computeFreeEdges();
      if (this.justAddedCode) {
        this.scrollCodeIntoView(this.justAddedCode);
        this.justAddedCode = null;
      }
      this.cdr.markForCheck();
      // A just-added node may not have painted yet, so its edges used an estimated size.
      // Re-measure once on the next tick so the geometry snaps to the real node size.
      const unmeasured =
        this.visualGraph.nodes.some(
          s => this.freePos[s.key] && !this.nodeSizes[s.key]
        ) || this.decisions.some(d => !this.nodeSizes['cond:' + d.tid]);
      if (unmeasured && !this.remeasureScheduled) {
        this.remeasureScheduled = true;
        setTimeout(() => {
          this.remeasureScheduled = false;
          if (!this.freeMode) return;
          this.measureNodes();
          this.computeFreeEdges();
          this.recomputeFreeSize();
          this.cdr.markForCheck();
        }, 80);
      }
    });
  }

  /**
   * Scroll the free canvas so a (newly added) node is clearly in view. The canvas can be
   * taller than the visible window, so place the node near the TOP of the canvas viewport
   * (rather than its centre) to guarantee it sits above the window fold.
   */
  private scrollCodeIntoView(code: string): void {
    const el = this.canvasRef?.nativeElement;
    if (!el || !this.freePos[code]) return;
    const sz = this.nodeSizes[code] ?? { w: 258, h: this.estH(code) };
    const x = this.renderX(code) * this.scale,
      y = this.renderY(code) * this.scale;
    el.scrollLeft = Math.max(
      0,
      x + (sz.w * this.scale) / 2 - el.clientWidth / 2
    );
    el.scrollTop = Math.max(0, y - 96);
  }
  private measureNodes(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;
    const sizes: Record<string, { w: number; h: number }> = {};
    // measure both state boxes and ◆ decision cards so edges anchor to their real edges
    canvas
      .querySelectorAll('.free-node, .iwb-decision')
      .forEach((n: Element) => {
        const code = n.getAttribute('data-code');
        const el = n as HTMLElement;
        if (code) sizes[code] = { w: el.offsetWidth, h: el.offsetHeight };
      });
    this.nodeSizes = sizes;
  }

  /** Pointer-drag a free-mode box to reposition it (click without movement still opens config). */
  onNodePointerDown(node: WorkflowVisualNode, e: PointerEvent): void {
    if (e.button !== 0) return; // only left-button drags (right-click → context menu)
    if (
      (e.target as HTMLElement).closest(
        '.achip, .iwb-anchor, button, .addbtn, .mini, input, textarea, select'
      )
    )
      return;
    const startX = e.clientX,
      startY = e.clientY;
    const key = node.key;
    const orig = this.freePos[key] ?? { x: 0, y: 0 };
    const scale = this.scale;
    let moved = false;
    const move = (ev: PointerEvent) => {
      if (
        !moved &&
        (Math.abs(ev.clientX - startX) > 4 || Math.abs(ev.clientY - startY) > 4)
      )
        moved = true;
      if (!moved) return;
      this.freePos = {
        ...this.freePos,
        [key]: {
          x: Math.round(orig.x + (ev.clientX - startX) / scale),
          y: Math.round(orig.y + (ev.clientY - startY) / scale),
        },
      };
      // No clamp — if the box moves past the left/top edge, the offset grows and we
      // scroll-compensate so the box stays under the pointer (infinite canvas, no wall).
      const prev = { x: this.off.x, y: this.off.y };
      this.updateOffset();
      const el = this.canvasRef?.nativeElement;
      if (el) {
        el.scrollLeft += (this.off.x - prev.x) * scale;
        el.scrollTop += (this.off.y - prev.y) * scale;
      }
      this.recomputeFreeSize();
      this.computeFreeEdges(); // pure (uses cached node sizes) → edges follow the drag live
      this.cdr.markForCheck();
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      if (!moved) return;
      const pos = this.freePos[key];
      if (node.key.startsWith('outcome:') && node.ref) {
        this.store.setEndStatePosition(node.ref.tid, node.ref.which, pos);
      } else {
        this.store.setPosition(node.stateCode, pos);
      }
      this.recomputeFreeSize();
      // swallow the click that follows a drag so it doesn't open the inspector
      const supp = (ce: Event) => {
        ce.stopPropagation();
        ce.preventDefault();
        document.removeEventListener('click', supp, true);
      };
      document.addEventListener('click', supp, true);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  }
  private recomputeFreeSize(): void {
    this.updateOffset();
    let w = 700,
      h = 460;
    for (const c of Object.keys(this.freePos)) {
      const sz = this.nodeSizes[c];
      w = Math.max(w, this.renderX(c) + (sz ? sz.w : 258) + this.MARGIN);
      h = Math.max(
        h,
        this.renderY(c) + (sz ? sz.h : this.estH(c)) + this.MARGIN
      );
    }
    this.freeSize = { w: Math.ceil(w), h: Math.ceil(h) };
    this.sizingDirty = true;
    this.cdr.markForCheck();
  }
  // ── render coordinates (logical position + offset) ──
  renderX(code: string): number {
    const p = this.freePos[code];
    return p ? p.x + this.off.x : 0;
  }
  renderY(code: string): number {
    const p = this.freePos[code];
    return p ? p.y + this.off.y : 0;
  }
  private updateOffset(): void {
    const xs = Object.values(this.freePos).map(p => p.x);
    const ys = Object.values(this.freePos).map(p => p.y);
    this.off = {
      x: this.MARGIN - (xs.length ? Math.min(...xs) : 0),
      y: this.MARGIN - (ys.length ? Math.min(...ys) : 0),
    };
  }

  // ── anchor geometry ──
  /** Render-space point of a named anchor on a state box. */
  /** Render-space centre + size of any node (state via freePos, or a ◆ diamond via condBox). */
  private boxCenterSize(
    code: string
  ): { cx: number; cy: number; w: number; h: number } | null {
    const cb = this.condBox[code];
    if (cb) return cb;
    const p = this.freePos[code];
    if (!p) return null;
    const s = this.sizeOf(code);
    return {
      cx: p.x + this.off.x + s.w / 2,
      cy: p.y + this.off.y + s.h / 2,
      w: s.w,
      h: s.h,
    };
  }
  anchorPoint(code: string, name: AnchorName): { x: number; y: number } | null {
    const b = this.boxCenterSize(code);
    if (!b) return null;
    const a = this.anchorFr[name];
    return {
      x: b.cx - b.w / 2 + a.fx * b.w,
      y: b.cy - b.h / 2 + a.fy * b.h,
    };
  }
  /** Default source/target anchors chosen by the boxes' relative position (states or ◆ diamond). */
  private chooseAnchors(
    srcCode: string,
    dstCode: string
  ): { src: AnchorName; dst: AnchorName } {
    const s = this.boxCenterSize(srcCode);
    const d = this.boxCenterSize(dstCode);
    if (!s || !d) return { src: 'b2', dst: 't2' };
    const dx = d.cx - s.cx,
      dy = d.cy - s.cy;
    if (Math.abs(dy) >= Math.abs(dx))
      return dy >= 0 ? { src: 'b2', dst: 't2' } : { src: 't2', dst: 'b2' };
    return dx >= 0 ? { src: 'r2', dst: 'l2' } : { src: 'l2', dst: 'r2' };
  }
  /** When both branches of a ◆ leave via the same anchor, spread them along that side. */
  private spreadBranchAnchors(
    a: AnchorName,
    b: AnchorName
  ): { one: AnchorName; two: AnchorName } {
    if (a !== b) return { one: a, two: b };
    const side: Record<string, [AnchorName, AnchorName]> = {
      b1: ['b1', 'b3'],
      b2: ['b1', 'b3'],
      b3: ['b1', 'b3'],
      t1: ['t1', 't3'],
      t2: ['t1', 't3'],
      t3: ['t1', 't3'],
      l1: ['l1', 'l3'],
      l2: ['l1', 'l3'],
      l3: ['l1', 'l3'],
      r1: ['r1', 'r3'],
      r2: ['r1', 'r3'],
      r3: ['r1', 'r3'],
    };
    const [one, two] = side[a] ?? ['b1', 'b3'];
    return { one, two };
  }
  /** Cubic control point that leaves an anchor along its outward normal. */
  private ctrl(
    name: AnchorName,
    x: number,
    y: number,
    off: number
  ): { x: number; y: number } {
    if (name[0] === 't') return { x, y: y - off };
    if (name[0] === 'b') return { x, y: y + off };
    if (name[0] === 'l') return { x: x - off, y };
    return { x: x + off, y };
  }

  /**
   * Recompute the free-mode connector edges (one per transition end-state) as SVG
   * cubic-bezier paths between chosen connection-point anchors. Self-guards via a
   * key so it never loops through change detection.
   */
  private computeFreeEdges(): void {
    const edges: FreeEdge[] = [];
    const condBox: typeof this.condBox = {};

    type EdgeOptions = {
      tid: string;
      label: string;
      actor: string;
      colour: string;
      isSystem: boolean;
      unauthorized: boolean;
      outcome: '' | '①' | '②';
      branch: '' | 'true' | 'false';
      defaultAnchors?: { src: AnchorName; dst: AnchorName };
    };
    type EdgeDraft = {
      id: string;
      srcKey: string;
      dstKey: string;
      srcCode: string;
      dstCode: string;
      opts: EdgeOptions;
    };
    const drafts: EdgeDraft[] = [];
    const addDraft = (
      id: string,
      srcKey: string,
      dstKey: string,
      srcCode: string,
      dstCode: string,
      opts: EdgeOptions
    ): void => {
      if (this.boxCenterSize(srcKey) && this.boxCenterSize(dstKey)) {
        drafts.push({ id, srcKey, dstKey, srcCode, dstCode, opts });
      }
    };

    // Decision diamonds must exist before endpoint allocation so they participate
    // in the same directional routing rules as state cards.
    for (const t of this.model.transitions) {
      if (!t.guard || !t.two) continue;
      const cc = this.condCenter(t);
      if (!cc) continue;
      const key = 'cond:' + t.id;
      const dsz = this.sizeOf(key);
      condBox[key] = { cx: cc.x, cy: cc.y, w: dsz.w, h: dsz.h };
    }
    this.condBox = condBox;

    for (const t of this.model.transitions) {
      const oneKey = this.visualKeyForOutcome(t, 'one');
      const twoKey = t.two ? this.visualKeyForOutcome(t, 'two') : null;
      const srcC = this.nodeCenter(t.startState);
      const oneC = this.nodeCenter(oneKey);
      const twoC = twoKey ? this.nodeCenter(twoKey) : null;
      const isSystem = !!t.authorisation?.systemAuthorised;
      const unauthorized = !isAuthorised(t);

      // ── guarded transition → route source → ◆ decision → (False ①, True ②) ──
      if (t.guard && t.two && srcC && oneC && twoC) {
        const key = 'cond:' + t.id;
        if (!condBox[key]) continue;
        addDraft(t.id + '→cond', t.startState, key, t.startState, key, {
          tid: t.id,
          label: t.event,
          actor: this.actorFor(t),
          colour: '#475467',
          isSystem,
          unauthorized,
          outcome: '',
          branch: '',
        });
        // spread the two branch exits so they don't overlap on the diamond
        const sp = this.spreadBranchAnchors(
          this.chooseAnchors(key, oneKey).src,
          this.chooseAnchors(key, twoKey as string).src
        );
        addDraft(
          t.id + '→one→' + t.one.stateCode,
          key,
          oneKey,
          key,
          t.one.stateCode,
          {
            tid: t.id,
            label: '',
            actor: '',
            colour: this.colour(t.one.stateCode),
            isSystem: false,
            unauthorized: false,
            outcome: '①',
            branch: 'false',
            defaultAnchors: {
              src: sp.one,
              dst: this.chooseAnchors(key, oneKey).dst,
            },
          }
        );
        addDraft(
          t.id + '→two→' + t.two.stateCode,
          key,
          twoKey as string,
          key,
          t.two.stateCode,
          {
            tid: t.id,
            label: '',
            actor: '',
            colour: this.colour(t.two.stateCode),
            isSystem: false,
            unauthorized: false,
            outcome: '②',
            branch: 'true',
            defaultAnchors: {
              src: sp.two,
              dst: this.chooseAnchors(key, twoKey as string).dst,
            },
          }
        );
        continue;
      }

      // ── normal transition (or guarded with missing positions) → direct edge(s) ──
      const dests: Array<{ es: IEndState; which: 'one' | 'two' }> = t.two
        ? [
            { es: t.one, which: 'one' },
            { es: t.two, which: 'two' },
          ]
        : [{ es: t.one, which: 'one' }];
      for (const { es, which } of dests) {
        if (this.isStuckSystemSelfLoop(t, es)) continue;
        addDraft(
          t.id + '→' + which + '→' + es.stateCode,
          t.startState,
          this.visualKeyForOutcome(t, which),
          t.startState,
          es.stateCode,
          {
            tid: t.id,
            label: t.event,
            actor: this.actorFor(t),
            colour: this.colour(es.stateCode),
            isSystem,
            unauthorized,
            outcome: t.two ? (which === 'one' ? '①' : '②') : '',
            branch: '',
          }
        );
      }
    }

    const resolved: Record<string, { src?: AnchorName; dst?: AnchorName }> = {};
    const usage: Record<string, Partial<Record<AnchorName, number>>> = {};
    type Endpoint = {
      edge: EdgeDraft;
      end: 'src' | 'dst';
      code: string;
      peerCode: string;
      preferred: AnchorName;
    };
    const automaticByNode: Record<string, Endpoint[]> = {};

    const reserve = (code: string, anchor: AnchorName): void => {
      const nodeUsage = (usage[code] = usage[code] ?? {});
      nodeUsage[anchor] = (nodeUsage[anchor] ?? 0) + 1;
    };

    // Manual endpoint positions win. All remaining endpoints are allocated as a
    // group per node, which prevents every incoming/outgoing edge using one port.
    for (const edge of drafts.filter(draft => draft.srcKey !== draft.dstKey)) {
      const defaults =
        edge.opts.defaultAnchors ??
        this.chooseAnchors(edge.srcKey, edge.dstKey);
      resolved[edge.id] = {};
      (['src', 'dst'] as const).forEach(end => {
        const override = this.freeEdgeAnchors[edge.id]?.[end];
        const code = end === 'src' ? edge.srcKey : edge.dstKey;
        const peerCode = end === 'src' ? edge.dstKey : edge.srcKey;
        if (override) {
          resolved[edge.id][end] = override;
          reserve(code, override);
          return;
        }
        (automaticByNode[code] = automaticByNode[code] ?? []).push({
          edge,
          end,
          code,
          peerCode,
          preferred: defaults[end],
        });
      });
    }

    const normal = (anchor: AnchorName): { x: number; y: number } => {
      const dir = this.dirOf(anchor);
      return { x: dir.dx, y: dir.dy };
    };
    const sideOf = (
      anchor: AnchorName
    ): 'top' | 'right' | 'bottom' | 'left' => {
      if (anchor[0] === 't') return 'top';
      if (anchor[0] === 'r') return 'right';
      if (anchor[0] === 'b') return 'bottom';
      return 'left';
    };
    const sidePorts: Record<
      ReturnType<typeof sideOf>,
      [AnchorName, AnchorName, AnchorName]
    > = {
      top: ['t1', 't2', 't3'],
      right: ['r1', 'r2', 'r3'],
      bottom: ['b1', 'b2', 'b3'],
      left: ['l1', 'l2', 'l3'],
    };
    Object.keys(automaticByNode).forEach(code => {
      const center = this.boxCenterSize(code);
      if (!center) return;
      const bySide = new Map<ReturnType<typeof sideOf>, Endpoint[]>();
      automaticByNode[code].forEach(endpoint => {
        const side = sideOf(endpoint.preferred);
        const group = bySide.get(side) ?? [];
        group.push(endpoint);
        bySide.set(side, group);
      });
      bySide.forEach((group, side) => {
        group.sort((a, b) => {
          const ac = this.boxCenterSize(a.peerCode);
          const bc = this.boxCenterSize(b.peerCode);
          const one = ac
            ? side === 'top' || side === 'bottom'
              ? ac.cx
              : ac.cy
            : 0;
          const two = bc
            ? side === 'top' || side === 'bottom'
              ? bc.cx
              : bc.cy
            : 0;
          return one - two || a.edge.id.localeCompare(b.edge.id);
        });

        const orderedPorts = sidePorts[side].filter(
          anchor => !(usage[code]?.[anchor] ?? 0)
        );
        let directPorts: AnchorName[] = [];
        if (group.length === 1 && orderedPorts.length === 3) {
          directPorts = [orderedPorts[1]];
        } else if (group.length === 2 && orderedPorts.length === 3) {
          directPorts = [orderedPorts[0], orderedPorts[2]];
        } else if (group.length <= orderedPorts.length) {
          directPorts = orderedPorts.slice(0, group.length);
        }

        group.forEach((endpoint, index) => {
          if (directPorts[index]) {
            resolved[endpoint.edge.id][endpoint.end] = directPorts[index];
            reserve(code, directPorts[index]);
            return;
          }
          const peer = this.boxCenterSize(endpoint.peerCode);
          if (!peer) return;
          const vx = peer.cx - center.cx;
          const vy = peer.cy - center.cy;
          const length = Math.max(1, Math.hypot(vx, vy));
          const desired = normal(endpoint.preferred);
          let selected = this.anchorNames[0];
          let bestScore = Number.POSITIVE_INFINITY;
          this.anchorNames.forEach(anchor => {
            const point = this.anchorPoint(code, anchor);
            if (!point) return;
            const n = normal(anchor);
            // Prefer the correct directional side, then the port closest to the
            // peer's bearing. A large occupancy penalty consumes all twelve ports
            // before reusing one on high-degree states.
            const directionPenalty =
              (1 - (n.x * desired.x + n.y * desired.y)) * 1200;
            const bearingPenalty =
              Math.abs(
                (point.x - center.cx) * vy - (point.y - center.cy) * vx
              ) / length;
            const occupancyPenalty = (usage[code]?.[anchor] ?? 0) * 10000;
            const score = directionPenalty + bearingPenalty + occupancyPenalty;
            if (score < bestScore) {
              bestScore = score;
              selected = anchor;
            }
          });
          resolved[endpoint.edge.id][endpoint.end] = selected;
          reserve(code, selected);
        });
      });
    });

    const parallelGroups = new Map<string, EdgeDraft[]>();
    drafts
      .filter(draft => draft.srcKey !== draft.dstKey)
      .forEach(draft => {
        const key = `${draft.srcKey}→${draft.dstKey}`;
        const group = parallelGroups.get(key) ?? [];
        group.push(draft);
        parallelGroups.set(key, group);
      });
    parallelGroups.forEach(group =>
      group.sort((a, b) => a.id.localeCompare(b.id))
    );
    const loopCount: Record<string, number> = {};
    const loopPairs: Array<{ src: AnchorName; dst: AnchorName }> = [
      { src: 'r1', dst: 'r3' },
      { src: 'l3', dst: 'l1' },
      { src: 't3', dst: 't1' },
      { src: 'b1', dst: 'b3' },
    ];
    const midpoint = (
      p0: { x: number; y: number },
      p1: { x: number; y: number },
      p2: { x: number; y: number },
      p3: { x: number; y: number }
    ): { x: number; y: number } => ({
      x: (p0.x + 3 * p1.x + 3 * p2.x + p3.x) / 8,
      y: (p0.y + 3 * p1.y + 3 * p2.y + p3.y) / 8,
    });
    type Rect = { left: number; right: number; top: number; bottom: number };
    const nodeRects: Rect[] = [
      ...this.visualGraph.nodes.map(node => node.key),
      ...Object.keys(condBox),
    ]
      .map(code => this.boxCenterSize(code))
      .filter(
        (box): box is { cx: number; cy: number; w: number; h: number } => !!box
      )
      .map(box => ({
        left: box.cx - box.w / 2 - 10,
        right: box.cx + box.w / 2 + 10,
        top: box.cy - box.h / 2 - 10,
        bottom: box.cy + box.h / 2 + 10,
      }));
    const labelRects: Rect[] = [];
    const overlapArea = (one: Rect, two: Rect): number =>
      Math.max(
        0,
        Math.min(one.right, two.right) - Math.max(one.left, two.left)
      ) *
      Math.max(
        0,
        Math.min(one.bottom, two.bottom) - Math.max(one.top, two.top)
      );
    const pointOnCurve = (
      p0: { x: number; y: number },
      p1: { x: number; y: number },
      p2: { x: number; y: number },
      p3: { x: number; y: number },
      t: number
    ): {
      point: { x: number; y: number };
      tangent: { x: number; y: number };
    } => {
      const u = 1 - t;
      return {
        point: {
          x:
            u ** 3 * p0.x +
            3 * u ** 2 * t * p1.x +
            3 * u * t ** 2 * p2.x +
            t ** 3 * p3.x,
          y:
            u ** 3 * p0.y +
            3 * u ** 2 * t * p1.y +
            3 * u * t ** 2 * p2.y +
            t ** 3 * p3.y,
        },
        tangent: {
          x:
            3 * u ** 2 * (p1.x - p0.x) +
            6 * u * t * (p2.x - p1.x) +
            3 * t ** 2 * (p3.x - p2.x),
          y:
            3 * u ** 2 * (p1.y - p0.y) +
            6 * u * t * (p2.y - p1.y) +
            3 * t ** 2 * (p3.y - p2.y),
        },
      };
    };
    const placeLabel = (
      edge: EdgeDraft,
      p0: { x: number; y: number },
      p1: { x: number; y: number },
      p2: { x: number; y: number },
      p3: { x: number; y: number }
    ): { x: number; y: number } => {
      const textLength = edge.opts.actor.length + edge.opts.label.length;
      const width = edge.opts.branch
        ? 92
        : Math.min(250, Math.max(132, textLength * 6.4 + 54));
      const height = edge.opts.branch ? 28 : 52;
      let best = midpoint(p0, p1, p2, p3);
      let bestRect: Rect = {
        left: best.x - width / 2,
        right: best.x + width / 2,
        top: best.y - height / 2,
        bottom: best.y + height / 2,
      };
      let bestScore = Number.POSITIVE_INFINITY;
      [
        0.5, 0.44, 0.56, 0.38, 0.62, 0.32, 0.68, 0.26, 0.74, 0.2, 0.8, 0.14,
        0.86,
      ].forEach(t => {
        const sample = pointOnCurve(p0, p1, p2, p3, t);
        const tangentLength = Math.max(
          1,
          Math.hypot(sample.tangent.x, sample.tangent.y)
        );
        const nx = -sample.tangent.y / tangentLength;
        const ny = sample.tangent.x / tangentLength;
        [0].forEach(offset => {
          const candidate = {
            x: sample.point.x + nx * offset,
            y: sample.point.y + ny * offset,
          };
          const rect: Rect = {
            left: candidate.x - width / 2,
            right: candidate.x + width / 2,
            top: candidate.y - height / 2,
            bottom: candidate.y + height / 2,
          };
          const nodeOverlap = nodeRects.reduce(
            (sum, node) => sum + overlapArea(rect, node),
            0
          );
          const labelOverlap = labelRects.reduce(
            (sum, label) => sum + overlapArea(rect, label),
            0
          );
          const score =
            nodeOverlap * 100000 +
            labelOverlap * 10000 +
            Math.abs(t - 0.5) * 500 +
            Math.abs(offset);
          if (score < bestScore) {
            bestScore = score;
            best = candidate;
            bestRect = rect;
          }
        });
      });
      labelRects.push(bestRect);
      return best;
    };

    drafts.forEach(edge => {
      let srcName: AnchorName;
      let dstName: AnchorName;
      let lane = 0;
      let controlOffset: number;
      if (edge.srcKey === edge.dstKey) {
        const index = loopCount[edge.srcKey] ?? 0;
        loopCount[edge.srcKey] = index + 1;
        const pair = loopPairs[index % loopPairs.length];
        srcName = this.freeEdgeAnchors[edge.id]?.src ?? pair.src;
        dstName = this.freeEdgeAnchors[edge.id]?.dst ?? pair.dst;
        controlOffset = 92 + Math.floor(index / loopPairs.length) * 36;
      } else {
        srcName = resolved[edge.id]?.src ?? 'b2';
        dstName = resolved[edge.id]?.dst ?? 't2';
        const group = parallelGroups.get(`${edge.srcKey}→${edge.dstKey}`) ?? [
          edge,
        ];
        lane = group.indexOf(edge) - (group.length - 1) / 2;
        const start = this.anchorPoint(edge.srcKey, srcName);
        const end = this.anchorPoint(edge.dstKey, dstName);
        controlOffset =
          start && end
            ? Math.max(48, Math.hypot(end.x - start.x, end.y - start.y) / 2.4)
            : 48;
      }
      const start = this.anchorPoint(edge.srcKey, srcName);
      const end = this.anchorPoint(edge.dstKey, dstName);
      if (!start || !end) return;
      const c1 = this.ctrl(srcName, start.x, start.y, controlOffset);
      const c2 = this.ctrl(dstName, end.x, end.y, controlOffset);
      if (edge.srcKey !== edge.dstKey && lane) {
        const distance = Math.max(
          1,
          Math.hypot(end.x - start.x, end.y - start.y)
        );
        const laneOffset = lane * 32;
        const nx = -(end.y - start.y) / distance;
        const ny = (end.x - start.x) / distance;
        c1.x += nx * laneOffset;
        c1.y += ny * laneOffset;
        c2.x += nx * laneOffset;
        c2.y += ny * laneOffset;
      }
      const middle = placeLabel(edge, start, c1, c2, end);
      edges.push({
        id: edge.id,
        tid: edge.opts.tid,
        srcCode: edge.srcCode,
        dstCode: edge.dstCode,
        srcKey: edge.srcKey,
        dstKey: edge.dstKey,
        srcName,
        dstName,
        sx: start.x,
        sy: start.y,
        ex: end.x,
        ey: end.y,
        d: `M ${start.x} ${start.y} C ${c1.x} ${c1.y} ${c2.x} ${c2.y} ${end.x} ${end.y}`,
        mx: middle.x,
        my: middle.y,
        label: edge.opts.label,
        actor: edge.opts.actor,
        colour: edge.opts.colour,
        isSystem: edge.opts.isSystem,
        unauthorized: edge.opts.unauthorized,
        outcome: edge.opts.outcome,
        branch: edge.opts.branch,
      });
    });

    const key = JSON.stringify(edges);
    if (key !== this._edgesKey) {
      this._edgesKey = key;
      this.freeEdges = edges;
      this.cdr.markForCheck();
    }
  }

  private isStuckSystemSelfLoop(t: ITransition, es: IEndState): boolean {
    if (WORKFLOW_VALIDATION_CONFIG.callbackEvents.includes(t.event)) {
      return false;
    }
    return (
      t.authorisation?.systemAuthorised === true &&
      t.startState === es.stateCode &&
      [t.one, t.two]
        .filter(Boolean)
        .every(outcome => outcome?.stateCode === t.startState)
    );
  }

  private actorFor(t: ITransition): string {
    const a = t.authorisation;
    if (a?.systemAuthorised) return 'System';
    if (a?.authorisedRoles?.length) return a.authorisedRoles.join(', ');
    return '—';
  }

  // ── connection points: drag from an anchor → add an event from that state ──
  onAnchorAddEvent(code: string, anchor: AnchorName, e: MouseEvent): void {
    e.stopPropagation();
    e.preventDefault();
    this.openAddEvent(code, e, anchor);
  }

  // ── drag an edge endpoint: onto its OWN box → re-route the anchor; onto ANOTHER node → move ──
  onEndpointDown(edge: FreeEdge, end: 'src' | 'dst', e: PointerEvent): void {
    e.stopPropagation();
    e.preventDefault();
    const ownKey = end === 'src' ? edge.srcKey : edge.dstKey;
    let dropKey: string | null = null;
    const move = (ev: PointerEvent) => {
      const pt = this.toLayerPoint(ev.clientX, ev.clientY);
      if (!pt) return;
      // Hovering a DIFFERENT node → this is a move; highlight it and skip anchor re-routing.
      const hit = this.nodeKeyAt(pt, ownKey);
      dropKey = hit;
      if (this.moveDropKey !== hit) {
        this.moveDropKey = hit;
        this.cdr.markForCheck();
      }
      if (hit) return;
      // Otherwise re-route the endpoint to the nearest anchor on its own box.
      const nearest = this.nearestAnchor(ownKey, pt);
      if (!nearest) return;
      const cur = this.freeEdgeAnchors[edge.id] || {};
      if (cur[end] === nearest) return;
      this.freeEdgeAnchors = {
        ...this.freeEdgeAnchors,
        [edge.id]: { ...cur, [end]: nearest },
      };
      this.computeFreeEdges();
      this.cdr.markForCheck();
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      const target = dropKey;
      if (this.moveDropKey) {
        this.moveDropKey = null;
        this.cdr.markForCheck();
      }
      if (target) this.beginMove(edge, end, target);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  }
  private toLayerPoint(
    clientX: number,
    clientY: number
  ): { x: number; y: number } | null {
    const flow = this.flowRef?.nativeElement;
    if (!flow) return null;
    const r = flow.getBoundingClientRect();
    return {
      x: (clientX - r.left) / this.scale,
      y: (clientY - r.top) / this.scale,
    };
  }
  private nearestAnchor(
    code: string,
    pt: { x: number; y: number }
  ): AnchorName | null {
    let best: AnchorName | null = null,
      bestD = Infinity;
    for (const name of this.anchorNames) {
      const a = this.anchorPoint(code, name);
      if (!a) continue;
      const dd = (a.x - pt.x) ** 2 + (a.y - pt.y) ** 2;
      if (dd < bestD) {
        bestD = dd;
        best = name;
      }
    }
    return best;
  }
  anchorStyle(code: string, name: AnchorName): { [k: string]: string } {
    const a = this.anchorFr[name];
    return { left: a.fx * 100 + '%', top: a.fy * 100 + '%' };
  }

  trackByCode(_i: number, s: { code: string }): string {
    return s.code;
  }
  trackByVisualNode(_i: number, node: WorkflowVisualNode): string {
    return node.key;
  }
  trackByEdge(_i: number, e: { id: string }): string {
    return e.id;
  }

  // ── reused wizard/v1 form managers ──
  openManager(m: 'notification' | 'office' | 'receipt'): void {
    this.manager = m;
  }
  closeManager(): void {
    this.manager = null;
  }

  /**
   * Merge an update emitted by a reused manager back into the store.
   * The notification / office / receipt forms emit the workflow ARRAY; other
   * deltas (full objects) are spread. Mirrors the wizard's onWorkflowUpdate so
   * the reused forms behave identically inside v2.
   */
  onManagerUpdate(updated: unknown): void {
    const base = (this.json as Record<string, unknown>) ?? {};
    const merged = Array.isArray(updated)
      ? { ...base, workflow: updated }
      : { ...base, ...(updated as Record<string, unknown>) };
    this.store.load(merged);
  }
}
